/**
 * Step 3 - Review & Confirm (Clara Vale Final)
 *
 * Four panels in a 2×2 grid: Vehicle, Product, Purchase, Customer.
 * Each panel owns exactly one concern. Centred at 780px.
 * On success: guarantee reference number in Cormorant Garamond 48px.
 */

import { useMemo, useState } from "react";
import { RegistrationFieldsPanel } from "./registration-fields-panel";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  Boxes,
  Car,
  Check,
  ExternalLink,
  Home,
  Loader2,
  HardHat,
  Package,
  Pencil,
  Receipt,
  ShieldCheck,
  Ship,
  UserCircle,
} from "lucide-react";
import api from "@/admin/lib/api";
import type { StructureSummary } from "@/admin/types/hosts";
import {
  getStructureDisplay,
  humaniseDetailValue,
  isOtherStructureValue,
} from "@/admin/pages/registrations/get-structure-display";
import { StructurePickerPanel } from "./structure-picker-panel";
import type {
  DuplicateAddressMatchSummary,
  DuplicateAddressDecision,
  RegistrationWizardIssue,
  SubmitRegistrationResult,
  WizardState,
} from "@/admin/pages/registrations/new-registration-page";
import type {
  PendingWarrantySummary,
  RegistrationFormMetadata,
  RegistrationResponse,
  WorkflowStateRef,
  WarrantyTemplateSummary,
  Holding,
} from "@/admin/types/products";
import { DetailCard, FieldRow } from "@/admin/components/shared/detail-card";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import { ConfirmationDialog } from "@/admin/components/shared/confirmation-dialog";
import { cn } from "@/shared/lib/utils";
import { googleFontImport, resolveDocumentFonts } from "@/shared/lib/fonts";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { useFormatting } from "@/admin/hooks/use-formatting";
import { useOrg } from "@/admin/hooks/use-org";
import { useExternalRoles } from "@/admin/hooks/use-external-roles";
import { usePartnershipDetail } from "@/admin/hooks/use-partnerships";
import { PartnerProfilePanel } from "@/admin/components/shared/partner-profile-panel";
import {
  useRegistrationFields,
  isDisclaimerRegistrationField,
  type RegistrationField,
} from "@/admin/hooks/use-registration-fields";
import { useEntityTypeByKey, useEntityTypes } from "@/admin/hooks/use-entities";
import { useCertificateDownloadEnabled } from "@/admin/hooks/use-registration-settings";
import { ConsumerConsentFields } from "@/shared/components/consumer-consent-fields";
import { missingRequiredConsents } from "@/shared/components/consumer-consent-kinds";
import type {
  EntityTableDisplayColumn,
  EntityTypeDefinition,
} from "@/admin/hooks/use-entities";
import {
  displayColumnLabel,
  registrationCardDisplayColumns,
  transactionCardDisplayColumns,
} from "@/admin/lib/entity-card-display";
import {
  columnTargetsProductName,
  resolveWizardRelationship,
} from "./review-card-columns";
import { buildRegistrationConfiguredFieldItems } from "@/admin/lib/registration-configured-fields";
import { RegistrationConfiguredFieldRows } from "@/admin/components/registration-configured-fields";
import {
  formatDurationFromMonths,
  formatRegistrationReference,
} from "@/admin/lib/formatting";
import { addressDisplay, isAddressLike } from "@/shared/lib/formatting";
import type { RuleHit } from "@/admin/hooks/use-validation-rules";
import type { ApprovalRuleHit } from "@/admin/hooks/use-approval-rules";
import { formatPropertyAddressFromWizardContext } from "@/admin/components/registration-wizard/format-property-display";
import { normaliseDetailCardDisplayValue } from "@/admin/components/shared/detail-card-formatting";
import {
  useDuplicateAddressMatches,
  useHolding,
  useProduct,
} from "@/admin/hooks/use-products";
import { useWarrantyTemplate } from "@/admin/hooks/use-warranties";
import {
  defineField,
  FILE_FIELD_TYPES,
  formatDisplayValue,
  isConditionallyRequired,
  isDisplayEmpty,
  isFieldVisible,
  isRequiredValueProvided,
  normaliseTemplateAttribute,
  type FieldType,
  type FormFieldMeta,
} from "@/shared/lib/forms";

// ── Props ─────────────────────────────────────────────────

interface StepReviewProps {
  state: WizardState;
  update: (payload: Partial<WizardState>) => void;
  next: () => void;
  back?: () => void;
  metadata: RegistrationFormMetadata | null;
  warrantyTemplates: WarrantyTemplateSummary[];
  onSubmit: () => Promise<SubmitRegistrationResult>;
  isSubmitting: boolean;
  onReset?: () => void;
  submissionWarningHits?: RuleHit[];
  submissionBlockedMessage?: string | null;
  /**
   * Approval rules that will fire when the registration is submitted.
   * When non-empty the wizard shows a confirmation modal explaining that
   * the registration will be created in the DRAFT state and queued for
   * approval via a workflow issue on the holding's lifecycle workflow
   * (see `apps.registrations.services.submission.submit_registration`).
   */
  submissionApprovalHits?: ApprovalRuleHit[];
  segments?: string[];
  returnTo?: string | null;
  /** When navigated from the Submit Claim wizard, the selected claim schema
   * key (e.g. "bfs"). Appended to the returnTo URL so the claim form
   * reopens with the correct schema. */
  claimSchemaKey?: string | null;
}

/** Format a captured RegistrationField value for display on review. */
function formatRegistrationFieldValue(
  field: RegistrationField,
  value: unknown,
  formatOptions?: { locale?: string; currency?: string },
): ReactNode | null {
  if (value === null || value === undefined || value === "") return null;
  if (
    field.field_type === "ENTITY_SEARCH" &&
    typeof value === "object" &&
    value !== null &&
    "name" in value
  ) {
    return (value as { name: string }).name;
  }
  if (typeof value === "object") return normaliseDetailCardDisplayValue(value);
  if (field.field_type === "ADDRESS") {
    const formatted = addressDisplay(value);
    return formatted || null;
  }
  if (isAddressLike(value)) {
    const formattedAddress = addressDisplay(value);
    if (formattedAddress) return formattedAddress;
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return value.map((v) => String(v)).join(", ");
  }
  const raw = String(value);
  if (field.field_type === "SELECT" || field.field_type === "MULTI_SELECT") {
    const options = (field.configuration_data?.options ?? []) as {
      value: string;
      label: string;
    }[];
    const match = options.find((o) => o.value === raw);
    if (match) return match.label;
  }
  return formatDisplayValue(
    defineField({
      code_name: field.code_name,
      display_name: field.display_name,
      field_type: field.field_type as FieldType,
      configuration_data: field.configuration_data,
    }),
    raw,
    formatOptions,
  );
}

function formatRegistrationFieldTextValue(
  field: RegistrationField,
  value: unknown,
): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (field.field_type === "ADDRESS") {
    return addressDisplay(value) || null;
  }
  if (isAddressLike(value)) {
    const formattedAddress = addressDisplay(value);
    if (formattedAddress) return formattedAddress;
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const values = value
      .map((item) => formatRegistrationFieldTextValue(field, item))
      .filter((item): item is string => Boolean(item));
    return values.length > 0 ? values.join(", ") : null;
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const fileLabel =
      record.file_name ||
      record.fileName ||
      record.uploaded_file_name ||
      record.name ||
      record.original_filename;
    return fileLabel ? String(fileLabel) : JSON.stringify(record);
  }
  const raw = String(value);
  if (field.field_type === "SELECT" || field.field_type === "MULTI_SELECT") {
    const options = (field.configuration_data?.options ?? []) as {
      value: string;
      label: string;
    }[];
    const match = options.find((option) => option.value === raw);
    if (match) return match.label;
  }
  return raw;
}

function formatProductFieldValue(
  field: RegistrationFormMetadata["fields"][number],
  value: unknown,
  formatOptions?: { locale?: string; currency?: string },
): ReactNode | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "object") return normaliseDetailCardDisplayValue(value);
  const options = field.options ?? [];
  const labelFor = (raw: unknown) => {
    const text = String(raw);
    return options.find((option) => option.value === text)?.label ?? text;
  };

  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return value.map(labelFor).join(", ");
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (field.field_type === "SELECT" || field.field_type === "MULTI_SELECT") {
    return labelFor(value);
  }
  if (isAddressLike(value)) {
    const formattedAddress = addressDisplay(value);
    if (formattedAddress) return formattedAddress;
  }
  return formatDisplayValue(
    defineField({
      code_name: field.code_name,
      display_name: field.display_name,
      field_type: field.field_type as FieldType,
      configuration_data: field.configuration_data,
    }),
    value,
    formatOptions,
  );
}

function resolveWizardRegistrationCardValue({
  state,
  selectedClass,
  column,
}: {
  state: WizardState;
  selectedClass: WarrantyTemplateSummary | undefined;
  column: EntityTableDisplayColumn;
}) {
  if (column.id.startsWith("transaction_attribute:")) {
    // Prefer the rich upload envelope so file/photo columns render a filename
    // link; fall back to the bare UUID in `transactionData`.
    const rich = getWizardValue(
      state.transactionUploads,
      column.attribute_code_name,
    );
    return rich !== ""
      ? rich
      : getWizardValue(state.transactionData, column.attribute_code_name);
  }

  const path = column.relationship_path?.length
    ? column.relationship_path
    : column.relationship_key
      ? [column.relationship_key]
      : [];
  let entityKey =
    path.length === 0
      ? column.entity_key || "holding"
      : path[0] === "holdings" || path[0] === "holding"
        ? "host"
        : "holding";

  for (const relationshipKey of path) {
    const nextEntityKey = resolveWizardRelationship(entityKey, relationshipKey);
    if (!nextEntityKey) return "";
    entityKey = nextEntityKey;
  }

  switch (entityKey) {
    case "host":
      return (
        getWizardValue(state.hostAttributeValues, column.attribute_code_name) ||
        getWizardValue(
          state.registrationFieldValues,
          column.attribute_code_name,
        )
      );
    case "holding":
      return getWizardValue(
        {
          ...state.earlyAttributes,
          ...state.dynamicAttributes,
          ...state.registrationFieldValues,
          ...state.transactionData,
          purchase_date: state.purchaseDate,
          purchase_price: state.purchasePrice,
          sales_person: state.salesPerson,
          reference: state.salesRefNumber,
        },
        column.attribute_code_name,
      );
    case "product":
      return getWizardProductValue(state, column.attribute_code_name);
    case "consumer":
      return getWizardValue(
        {
          name: [state.consumerFirstName, state.consumerLastName]
            .filter(Boolean)
            .join(" "),
          first_name: state.consumerFirstName,
          last_name: state.consumerLastName,
          email: customerOwnEmail(state),
          phone: state.consumerPhone,
          phone_number: state.consumerPhone,
          address: state.address,
        },
        column.attribute_code_name,
      );
    case "organisation":
      return getWizardValue(
        {
          name: state.partnershipName,
          partnership_uuid: state.partnershipUuid,
        },
        column.attribute_code_name,
      );
    case "warranty":
      return getWizardValue(
        {
          ...selectedClass,
          name: selectedClass?.name,
        },
        column.attribute_code_name,
      );
    default:
      return "";
  }
}

function isTransactionDisplayColumn(column: EntityTableDisplayColumn) {
  return column.id.startsWith("transaction_attribute:");
}

type TransactionAttribute = NonNullable<
  RegistrationFormMetadata["transaction_template"]
>["attributes"][number];

/**
 * Resolve the captured value for a transaction-template attribute from
 * wizard state. Built-in `code_name`s map to dedicated `WizardState`
 * fields; everything else is read from the generic `transactionData`
 * bag keyed by `code_name`.
 */
function resolveTransactionAttrValue(
  state: WizardState,
  codeName: string,
): unknown {
  switch (codeName) {
    case "purchase_date":
      return state.purchaseDate;
    case "warranty_start_date":
      return state.warrantyStartDate;
    case "purchase_price":
      return state.purchasePrice;
    case "sales_person":
      return state.salesPerson;
    case "sales_reference":
      return state.salesRefNumber;
    default:
      // Prefer the rich upload envelope (filename + URL) for file/photo
      // attributes so the review renders a link; `transactionData` only has
      // the bare UUID. `transactionUploads` is populated for file fields only.
      return (
        state.transactionUploads?.[codeName] ??
        state.transactionData?.[codeName]
      );
  }
}

/**
 * Format a transaction-template attribute value for the review card.
 *
 * Delegates type-aware formatting (currency, date, number, boolean,
 * SELECT/MULTI_SELECT label resolution, file) to the canonical
 * `formatDisplayValue`. The `attr` is normalised via
 * `normaliseTemplateAttribute` so that `configuration_data.options`
 * is hoisted to `field.options` for label lookup.
 *
 * Address values get formatted via `addressDisplay` first since
 * they are stored as nested objects with no canonical `field_type`
 * presence on every legacy template. Other plain objects fall back to
 * `normaliseDetailCardDisplayValue`. Returns `null` for empty values.
 */
function formatTransactionAttrValue(
  attr: TransactionAttribute,
  value: unknown,
  options?: { locale?: string; currency?: string },
): ReactNode | null {
  if (value === null || value === undefined || value === "") return null;

  if (attr.field_type === "ADDRESS") {
    return addressDisplay(value) || null;
  }
  // Legacy fallback: only treat the value as an address if it actually
  // looks like one. ``addressDisplay`` happily echoes plain strings back
  // (it puts them in ``address_line_1``), so without ``isAddressLike``
  // we'd short-circuit dates/prices/text and skip ``formatDisplayValue``.
  if (isAddressLike(value)) {
    const formattedAddress = addressDisplay(value);
    if (formattedAddress) return formattedAddress;
  }

  const field: FormFieldMeta = normaliseTemplateAttribute({
    uuid: attr.uuid,
    code_name: attr.code_name,
    display_name: attr.display_name,
    field_type: attr.field_type,
    configuration_data: attr.configuration_data,
    order: attr.order,
  });

  if (
    typeof value === "object" &&
    !Array.isArray(value) &&
    !FILE_FIELD_TYPES.has(field.field_type.toUpperCase())
  ) {
    return normaliseDetailCardDisplayValue(value);
  }

  return formatDisplayValue(field, value, options);
}

type ReviewSectionKey =
  | "host"
  | "contractor"
  | "product"
  | "warranty"
  | "purchase"
  | "customer"
  | "registration_details";

// WPS-114: codes rendered as dedicated, always-resolved rows on the Purchase
// review card (install date from the captured holding attribute, warranty
// start from the submit cascade). Configured display columns / transaction
// attributes for these are suppressed so they never render as an empty "-".
const WPS114_EXPLICIT_PURCHASE_CODES = new Set([
  "installation_date",
  "warranty_start_date",
]);

function reviewSectionsForSegments(segments: string[]): ReviewSectionKey[] {
  const out: ReviewSectionKey[] = [];
  const add = (section: ReviewSectionKey) => {
    if (!out.includes(section)) out.push(section);
  };
  const source =
    segments.length > 0
      ? segments.filter((segment) => segment !== "review")
      : ["host", "product", "purchase", "customer"];

  for (const segment of source) {
    switch (segment) {
      case "host_context":
      case "host":
        add("host");
        break;
      case "host_product":
        add("host");
        add("product");
        break;
      case "product":
        add("product");
        break;
      case "warranty":
        add("warranty");
        break;
      case "purchase":
        add("purchase");
        break;
      case "customer":
        add("customer");
        break;
      case "purchase_customer":
        add("purchase");
        add("customer");
        break;
      case "registration_details":
        add("registration_details");
        break;
      default:
        break;
    }
  }

  return out;
}

function stepIndexForSection(segments: string[], section: ReviewSectionKey) {
  const candidates: Record<ReviewSectionKey, string[]> = {
    host: ["host_context", "host", "host_product"],
    product: ["product", "host_product"],
    warranty: ["warranty"],
    purchase: ["purchase", "purchase_customer"],
    customer: ["customer", "purchase_customer"],
    registration_details: ["registration_details"],
  };
  const index = segments.findIndex((segment) =>
    candidates[section].includes(segment),
  );
  return index >= 0 ? index : 0;
}

function getWizardProductValue(state: WizardState, codeName: string) {
  const productData =
    typeof state.selectedProduct?.product_data === "object" &&
    state.selectedProduct.product_data !== null
      ? state.selectedProduct.product_data
      : {};
  return getWizardValue(
    {
      ...state.selectedProduct,
      ...productData,
      name:
        state.selectedProduct?.parent_product?.cached_short_name ||
        getWizardValue(productData, "name") ||
        state.selectedProduct?.cached_short_name,
      product_template: state.selectedProduct?.product_template?.name,
    },
    codeName,
  );
}

function getWizardValue(record: unknown, codeName: string) {
  if (!record || typeof record !== "object") return "";
  const value = (record as Record<string, unknown>)[codeName];
  return value === null || value === undefined || value === "" ? "" : value;
}

function findColumnAttribute(
  column: EntityTableDisplayColumn,
  entityTypes: EntityTypeDefinition[],
  metadata: RegistrationFormMetadata | null,
) {
  if (isTransactionDisplayColumn(column)) {
    return metadata?.transaction_template?.attributes.find(
      (attribute) => attribute.code_name === column.attribute_code_name,
    );
  }

  return entityTypes
    .find((entity) => entity.key === column.entity_key)
    ?.attributes.find(
      (attribute) => attribute.code_name === column.attribute_code_name,
    );
}

function formatWizardDisplayColumnValue({
  value,
  column,
  entityTypes,
  metadata,
  locale,
  currency,
}: {
  value: unknown;
  column: EntityTableDisplayColumn;
  entityTypes: EntityTypeDefinition[];
  metadata: RegistrationFormMetadata | null;
  locale?: string;
  currency?: string;
}) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return normaliseDetailCardDisplayValue(value);

  const attribute = findColumnAttribute(column, entityTypes, metadata);
  const field = defineField({
    code_name: column.id,
    display_name: displayColumnLabel(column),
    field_type: (attribute?.field_type ?? "TEXT") as FieldType,
    configuration_data: attribute?.configuration_data,
  });

  return formatDisplayValue(field, value, { locale, currency });
}

// ── Helpers ───────────────────────────────────────────────

/** Format a currency string value (from form input) using provided formatter. */
function fmtCurrencyStr(
  amount: string,
  formatter: (n: number) => string,
): string {
  if (!amount) return "";
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  return formatter(num);
}

function formatWarrantyTypeLabel(warrantyType?: string | null) {
  if (!warrantyType) return undefined;
  if (warrantyType === "MANUFACTURER_WARRANTY") return "Standard";

  return warrantyType
    .replace(/_WARRANTY$/, "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (character) => character.toUpperCase());
}

function formatWarrantyStatusLabel(status?: string | null) {
  if (!status) return undefined;
  const raw = status.includes(".")
    ? (status.split(".").pop() ?? status)
    : status;
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

function formatWarrantyAttributeValue(
  fieldType: string | undefined,
  value: unknown,
  fmtDate: (iso: string) => string,
): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (fieldType === "ADDRESS") return addressDisplay(value) || null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return value.map((item) => String(item)).join(", ");
  }
  if (
    typeof value === "string" &&
    (fieldType === "DATE" ||
      fieldType === "DATE_TIME" ||
      fieldType === "DATETIME")
  ) {
    return fmtDate(value);
  }
  return String(value);
}

function ReviewEditButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      onClick={onClick}
      aria-label="Edit section"
      className="h-6 w-6 cursor-pointer font-light text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
    >
      <Pencil className="h-3 w-3" />
    </Button>
  );
}

const DUPLICATE_DECISION_OPTIONS: Array<{
  key: DuplicateAddressDecision;
  label: string;
  description: string;
}> = [
  {
    key: "merge",
    label: "Add to this structure and cancel existing warranties",
    description:
      "Commit this registration to the matched structure. Existing warranties for this structure will be cancelled.",
  },
  {
    key: "add_new_product",
    label: "Continue with this property",
    description:
      "Register against this property: add a new structure, or add a product to one of its existing structures. Existing records are kept.",
  },
  {
    key: "confirmed_duplicate",
    label: "Cancel, this is a duplicate",
    description: "Abandon this submission. Nothing new will be created.",
  },
];

/** The typed email, unless it is marked not the customer's (then it is kept
 *  as the registrant's and the customer gets a stand-in). */
function customerOwnEmail(state: WizardState): string {
  return state.consumerEmailIsOwners === false ? "" : state.consumerEmail;
}

function consumerDisplayName(
  consumer:
    | { first_name?: string; last_name?: string; email?: string }
    | null
    | undefined,
): string | null {
  if (!consumer) return null;
  const full = [consumer.first_name, consumer.last_name]
    .filter((part) => part && part.trim())
    .join(" ")
    .trim();
  return full || consumer.email || null;
}

function formatMailingAddress(
  address: import("@/shared/lib/formatting").Address | null | undefined,
): string {
  return addressDisplay(address);
}

function RadioMark({ selected }: { selected: boolean }) {
  return (
    <span
      className={cn(
        "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-[1.5px]",
        selected
          ? "border-[var(--green)] bg-[var(--green)]"
          : "border-[var(--rule-strong)] bg-transparent",
      )}
    >
      {selected ? (
        <Check className="h-3 w-3 text-[var(--bg)]" strokeWidth={3} />
      ) : null}
    </span>
  );
}

function DuplicateMatchRow({
  match,
  selected,
  onSelect,
}: {
  match: DuplicateAddressMatchSummary;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const { data: holding, isLoading } = useHolding(match.holding_uuid);

  // Don't render if the holding is not active
  if (holding && !isHoldingActive(holding)) {
    return null;
  }

  const productName =
    match.product_name ||
    holding?.product?.cached_short_name ||
    (isLoading ? "Loading product…" : "Existing registration");
  const owner =
    match.owner_name ||
    consumerDisplayName(holding?.consumer_account) ||
    match.owner_email ||
    "-";
  const structureType = match.structure_values?.structure_type as
    string | undefined;
  const otherStructureType = match.structure_values?.other_structure_type as
    string | undefined;
  const structureLabel = structureType
    ? isOtherStructureValue(structureType) && otherStructureType
      ? t("registrations.otherStructureLabel", "Other: {{type}}", {
          type: humaniseDetailValue(otherStructureType),
        })
      : humaniseDetailValue(structureType)
    : null;
  return (
    <Button
      type="button"
      variant="outline"
      onClick={onSelect}
      className={cn(
        "h-auto w-full items-start justify-start whitespace-normal rounded-[6px] border-[0.5px] px-3 py-2.5 text-left font-light hover:bg-[var(--bg-subtle)]",
        selected
          ? "border-[var(--green)] bg-[var(--green-bg)]"
          : "border-[var(--rule)] bg-[var(--bg)]",
      )}
    >
      <RadioMark selected={selected} />
      <span className="ml-3 flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline justify-between gap-3">
          <span className="truncate text-[13px] font-light text-[var(--ink)]">
            {productName}
          </span>
          <span className="shrink-0 text-[11px] text-[var(--ink-3)]">
            {owner}
          </span>
        </span>
        {structureLabel && (
          <span className="text-[11px] text-[var(--ink-3)]">
            {structureLabel}
          </span>
        )}
      </span>
    </Button>
  );
}

function ExistingRegistrationRow({
  match,
}: {
  match: DuplicateAddressMatchSummary;
}) {
  const { formatDateLong } = useFormatting();
  const { t } = useTranslation();
  const structureType = match.structure_values?.structure_type as
    string | undefined;
  const otherStructureType = match.structure_values?.other_structure_type as
    string | undefined;
  const structureLabel = structureType
    ? isOtherStructureValue(structureType) && otherStructureType
      ? t("registrations.otherStructureLabel", "Other: {{type}}", {
          type: humaniseDetailValue(otherStructureType),
        })
      : humaniseDetailValue(structureType)
    : null;
  const productName = match.product_name || "Unknown product";
  const status = match.registration_status;
  const statusLabel = status?.state_label || "Unknown";
  const statusColor = status?.state_color || "#9a9a94";
  // WPS-150 registration-view enrichment: the associated Claim # and Installed
  // Date shown under the address for each duplicate registration result.
  const installedDate = match.installation_date
    ? formatDateLong(match.installation_date)
    : null;

  return (
    <div className="flex items-center gap-3 rounded-[4px] bg-[var(--bg)] px-3 py-2">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[12.5px] font-light text-[var(--ink)]">
          {productName}
        </span>
        {structureLabel && (
          <span className="truncate text-[11px] text-[var(--ink-3)]">
            {structureLabel}
          </span>
        )}
        {match.claim_reference && (
          <span className="truncate text-[11px] text-[var(--ink-3)]">
            {t(
              "registration.duplicate_row.claim_number",
              "Claim #: {{reference}}",
              {
                reference: match.claim_reference,
              },
            )}
          </span>
        )}
        {installedDate && (
          <span className="truncate text-[11px] text-[var(--ink-3)]">
            {t(
              "registration.duplicate_row.installed_date",
              "Installed Date: {{date}}",
              {
                date: installedDate,
              },
            )}
          </span>
        )}
      </div>
      <span
        className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-light"
        style={{
          backgroundColor: `${statusColor}18`,
          color: statusColor,
        }}
      >
        {statusLabel}
      </span>
    </div>
  );
}

function DuplicateAddressReviewWarning({
  issue,
  onResolve,
}: {
  issue: RegistrationWizardIssue;
  onResolve: () => void;
}) {
  return (
    <div className="border-b-[0.5px] border-b-[var(--rule-light)] bg-[rgba(245,158,11,0.08)] px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(180,83,9)]" />
          <div className="min-w-0">
            <p className="m-0 text-[11px] font-light uppercase tracking-[0.12em] text-[rgb(146,64,14)]">
              Possible duplicate
            </p>
            <p className="mt-1 text-[12.5px] leading-[1.45] text-[rgb(120,53,15)]">
              {issue.message}
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={onResolve}
          className="h-7 shrink-0 rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 text-[10px] font-light uppercase tracking-[0.1em] text-[var(--ink)]"
        >
          Resolve
        </Button>
      </div>
    </div>
  );
}

// ── Duplicate "continue with this property" structure paths ──────────────
//
// After choosing to continue a duplicate-address registration against the
// matched property, the operator picks ONE of two paths (WP1-34):
//   (A) add a new structure to the property (reuses StructurePickerPanel)
//   (B) add a product to one of the property's existing structures.

type DuplicateStructurePath = "new_structure" | "existing_structure";

const STRUCTURE_PATH_OPTIONS: Array<{
  key: DuplicateStructurePath;
  label: string;
  description: string;
}> = [
  {
    key: "new_structure",
    label: "Add a new structure to this property",
    description:
      "Create another structure (e.g. a garage, barn, or pool house) on this property, then register the product against it.",
  },
  {
    key: "existing_structure",
    label: "Add a product to an existing structure",
    description:
      "Register the product against one of this property's existing structures.",
  },
];

function DuplicateExistingStructureList({
  hostUuid,
  selectedStructureUuid,
  onSelect,
}: {
  hostUuid: string | null;
  selectedStructureUuid: string | null;
  onSelect: (uuid: string) => void;
}) {
  const { t } = useTranslation();
  const { data: structures = [], isLoading } = useQuery<StructureSummary[]>({
    queryKey: ["host-structures", hostUuid],
    queryFn: () =>
      api
        .get<StructureSummary[]>(`/hosts/${hostUuid}/structures/`)
        .then((r) => r.data ?? []),
    enabled: !!hostUuid,
  });

  if (isLoading) {
    return (
      <p className="py-2 text-[12px] font-light text-[var(--ink-3)]">
        Looking up existing structures...
      </p>
    );
  }
  if (structures.length === 0) {
    return (
      <p className="py-2 text-[12px] font-light leading-[1.45] text-[var(--ink-3)]">
        This property has no existing structures. Use "Add a new structure"
        instead.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {structures.map((structure) => {
        const display = getStructureDisplay(structure, undefined, t);
        const selected = selectedStructureUuid === structure.uuid;
        return (
          <Button
            key={structure.uuid}
            type="button"
            variant="ghost"
            onClick={() => onSelect(structure.uuid)}
            aria-pressed={selected}
            className={cn(
              "flex h-auto w-full items-center justify-start gap-3 rounded-[10px] border-[0.5px] px-3.5 py-3 text-left",
              selected
                ? "border-[var(--green-border)] bg-[var(--green-bg)] hover:bg-[var(--green-bg)]"
                : "border-[var(--rule)] bg-[var(--bg)] hover:border-[var(--green-border)] hover:bg-[var(--bg-subtle)]",
            )}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--bg-subtle)] text-[var(--ink-3)]">
              <Boxes size={15} className="text-inherit" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] text-[var(--ink)]">
                {display.name}
              </span>
              {display.meta ? (
                <span className="mt-0.5 block truncate text-[11px] font-light text-[var(--ink-3)]">
                  {display.meta}
                </span>
              ) : null}
            </span>
            {selected ? (
              <Check
                size={16}
                className="shrink-0 text-[var(--green)]"
                strokeWidth={2.25}
              />
            ) : null}
          </Button>
        );
      })}
    </div>
  );
}

function DuplicateStructurePathStep({
  hostUuid,
  state,
  update,
  path,
  onPathChange,
  selectedStructureUuid,
  onSelectStructure,
}: {
  hostUuid: string | null;
  state: WizardState;
  update: (payload: Partial<WizardState>) => void;
  path: DuplicateStructurePath | null;
  onPathChange: (path: DuplicateStructurePath) => void;
  selectedStructureUuid: string | null;
  onSelectStructure: (uuid: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <p className="clara-section-label">How do you want to register it?</p>
      <div className="flex flex-col gap-1.5">
        {STRUCTURE_PATH_OPTIONS.map((option) => {
          const selected = path === option.key;
          return (
            <div
              key={option.key}
              className={cn(
                "flex flex-col overflow-hidden rounded-[6px] border-[0.5px]",
                selected
                  ? "border-[var(--green)] bg-[var(--green-bg)]"
                  : "border-[var(--rule)] bg-[var(--bg)]",
              )}
            >
              <Button
                type="button"
                variant="ghost"
                onClick={() => onPathChange(option.key)}
                className="h-auto w-full items-start justify-start whitespace-normal rounded-none border-0 bg-transparent px-3 py-2.5 text-left font-light hover:bg-[rgba(0,0,0,0.02)]"
              >
                <RadioMark selected={selected} />
                <span className="ml-3 flex min-w-0 flex-1 flex-col">
                  <span className="block text-[13px] font-light text-[var(--ink)]">
                    {option.label}
                  </span>
                  <span className="mt-0.5 block break-words text-[11.5px] font-light leading-[1.4] text-[var(--ink-3)]">
                    {option.description}
                  </span>
                </span>
              </Button>
              {selected && option.key === "existing_structure" ? (
                <div className="border-t-[0.5px] border-t-[rgba(0,0,0,0.08)] bg-[var(--bg)] px-3 pb-3 pt-2.5">
                  <DuplicateExistingStructureList
                    hostUuid={hostUuid}
                    selectedStructureUuid={selectedStructureUuid}
                    onSelect={onSelectStructure}
                  />
                </div>
              ) : null}
              {selected && option.key === "new_structure" ? (
                <div className="border-t-[0.5px] border-t-[rgba(0,0,0,0.08)] bg-[var(--bg)] px-3 pb-3 pt-2.5">
                  <StructurePickerPanel state={state} update={update} />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DuplicateAddressDecisionModal({
  open,
  propertyAddress,
  duplicateMatches,
  hostLookupData,
  state,
  update,
  onClose,
  onGoBack,
  onPrepareHost,
  onResolve,
}: {
  open: boolean;
  issue: RegistrationWizardIssue | null;
  propertyAddress: string;
  duplicateMatches: DuplicateAddressMatchSummary[];
  hostLookupData?: Record<string, unknown> | null;
  // Wizard state + updater so the "add a new structure" path can reuse the
  // shared StructurePickerPanel against the matched property host.
  state: WizardState;
  update: (payload: Partial<WizardState>) => void;
  onClose: () => void;
  // Dismiss the warning and return to the address/host step to edit it.
  onGoBack: () => void;
  // Point the wizard's structure picker at the matched property host (or clear
  // it) when entering/leaving the "continue with this property" flow.
  onPrepareHost: (hostUuid: string | null) => void;
  onResolve: (
    decision: DuplicateAddressDecision,
    label: string,
    notes: string,
    selectedHoldingUuid?: string,
    resolutionContext?: {
      matches: DuplicateAddressMatchSummary[];
      selectedMatch?: DuplicateAddressMatchSummary | null;
      structurePath?: "new_structure" | "existing_structure" | null;
      selectedHostUuid?: string | null;
      selectedStructureUuid?: string | null;
    },
  ) => void;
}) {
  const [choice, setChoice] = useState<DuplicateAddressDecision | null>(null);
  // Two-screen flow: when the operator chooses "Continue with this property"
  // (add_new_product), advance to the structure-path screen.
  const [screen, setScreen] = useState<"decision" | "structure">("decision");
  const [structurePath, setStructurePath] =
    useState<DuplicateStructurePath | null>(null);
  const [selectedStructureUuid, setSelectedStructureUuid] = useState<
    string | null
  >(null);
  const [prevOpen, setPrevOpen] = useState(open);
  const shouldLookup = open && !!hostLookupData;
  const { data: lookupMatches } = useDuplicateAddressMatches(
    shouldLookup ? (hostLookupData ?? null) : null,
    { enabled: shouldLookup },
  );
  const effectiveMatches = useMemo<DuplicateAddressMatchSummary[]>(() => {
    const byUuid = new Map<string, DuplicateAddressMatchSummary>();
    for (const match of [...(lookupMatches ?? []), ...duplicateMatches]) {
      if (!match.holding_uuid || byUuid.has(match.holding_uuid)) {
        continue;
      }
      byUuid.set(match.holding_uuid, match);
    }
    return [...byUuid.values()];
  }, [duplicateMatches, lookupMatches]);
  const [selectedMatchUuid, setSelectedMatchUuid] = useState<string | null>(
    null,
  );
  const selectedEffectiveMatchUuid =
    effectiveMatches.find((match) => match.holding_uuid === selectedMatchUuid)
      ?.holding_uuid ??
    effectiveMatches[0]?.holding_uuid ??
    null;
  const existingRecordTargetUuid =
    selectedEffectiveMatchUuid ?? effectiveMatches[0]?.holding_uuid ?? null;
  const mergeReady =
    choice === "merge" &&
    (selectedEffectiveMatchUuid !== null || effectiveMatches.length === 0);
  const selectedOption = DUPLICATE_DECISION_OPTIONS.find(
    (option) => option.key === choice,
  );

  // The matched property host the continued registration attaches to.
  const matchedHostUuid = useMemo(() => {
    const selected = effectiveMatches.find(
      (match) => match.holding_uuid === selectedEffectiveMatchUuid,
    );
    return selected?.host_uuid ?? effectiveMatches[0]?.host_uuid ?? null;
  }, [effectiveMatches, selectedEffectiveMatchUuid]);

  // Reset the two-screen flow whenever the modal closes. Adjusting state
  // during render (React's recommended pattern) instead of in an effect
  // avoids the cascading re-render that a synchronous setState-in-effect
  // triggers.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open) {
      setScreen("decision");
      setChoice(null);
      setStructurePath(null);
      setSelectedStructureUuid(null);
    }
  }

  const onStructureScreen = screen === "structure";
  const structurePathReady =
    structurePath === "new_structure" ||
    (structurePath === "existing_structure" && !!selectedStructureUuid);
  const canSubmit = onStructureScreen
    ? structurePathReady
    : mergeReady || choice === "confirmed_duplicate";

  const existingRecordHref = existingRecordTargetUuid
    ? `/registrations/${existingRecordTargetUuid}`
    : null;

  const handleResolve = () => {
    if (onStructureScreen) {
      if (!structurePathReady) return;
      // The property being continued has a current owner. Carry that match
      // through so the Customer step can pre-fill with the property owner
      // (WPS-38) instead of leaving the operator to retype known details.
      const continuationMatch =
        effectiveMatches.find((match) => match.host_uuid === matchedHostUuid) ??
        effectiveMatches[0] ??
        null;
      onResolve(
        "add_new_product",
        structurePath === "new_structure"
          ? "Continue · add new structure"
          : "Continue · add to existing structure",
        "",
        undefined,
        {
          matches: effectiveMatches,
          selectedMatch: continuationMatch,
          structurePath,
          selectedHostUuid: matchedHostUuid,
          selectedStructureUuid:
            structurePath === "existing_structure"
              ? selectedStructureUuid
              : null,
        },
      );
      return;
    }
    if (!choice || !selectedOption || !canSubmit) return;
    onResolve(
      choice,
      selectedOption.label,
      "",
      choice === "merge"
        ? (selectedEffectiveMatchUuid ?? undefined)
        : undefined,
      {
        matches: effectiveMatches,
        selectedMatch:
          choice === "merge"
            ? (effectiveMatches.find(
                (match) => match.holding_uuid === selectedEffectiveMatchUuid,
              ) ?? null)
            : null,
      },
    );
    setChoice(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent
        bare
        className="max-h-[min(88vh,860px)] overflow-hidden sm:max-w-[560px]"
      >
        <DialogHeader className="gap-0 px-6 pb-4 pt-5 border-b-[0.5px] border-b-[var(--rule)]">
          <p className="clara-section-label">Possible duplicate</p>
          <DialogTitle className="clara-heading mt-1 text-[22px] leading-tight">
            {propertyAddress || "Address unavailable"}
          </DialogTitle>
          <DialogDescription className="mt-1 text-[12px] leading-snug text-[var(--ink-3)]">
            This registration matches one already on file. Confirm whether to
            merge or proceed.
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[calc(min(88vh,860px)-180px)] flex-col gap-4 overflow-y-auto px-6 py-4">
          <div className="rounded-[6px] border-[0.5px] border-[rgba(180,83,9,0.35)] bg-[rgba(245,158,11,0.08)] px-3.5 py-3">
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(180,83,9)]" />
              <div className="min-w-0">
                <p className="m-0 text-[10px] font-light uppercase tracking-[0.14em] text-[rgb(146,64,14)]">
                  Why you're seeing this
                </p>
                <p className="mt-1 text-[12.5px] leading-[1.45] text-[rgb(120,53,15)]">
                  This registration matches an existing record based on property
                  address.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
            <div className="px-4 pb-1 pt-3">
              <p className="m-0 text-[9.5px] font-light uppercase tracking-[0.16em] text-[var(--ink-3)]">
                Existing registrations at this property
              </p>
            </div>
            {effectiveMatches.length > 0 ? (
              <div className="flex flex-col gap-px px-3 pb-3 pt-1.5">
                {effectiveMatches.map((match) => (
                  <ExistingRegistrationRow
                    key={match.holding_uuid}
                    match={match}
                  />
                ))}
              </div>
            ) : (
              <p className="px-4 pb-3 pt-1 text-[12px] font-light text-[var(--ink-3)]">
                No existing registrations found.
              </p>
            )}
          </div>

          {onStructureScreen ? (
            <DuplicateStructurePathStep
              hostUuid={matchedHostUuid}
              state={state}
              update={update}
              path={structurePath}
              onPathChange={setStructurePath}
              selectedStructureUuid={selectedStructureUuid}
              onSelectStructure={setSelectedStructureUuid}
            />
          ) : (
            <div>
              <p className="clara-section-label mb-2 mt-2">
                How should we handle this?
              </p>
              <div className="flex flex-col gap-1.5">
                {DUPLICATE_DECISION_OPTIONS.map((option) => {
                  const selected = choice === option.key;
                  const isMergeOption = option.key === "merge";
                  const showMergeSection = isMergeOption && selected;
                  return (
                    <div
                      key={option.key}
                      className={cn(
                        "flex flex-col overflow-hidden rounded-[6px] border-[0.5px]",
                        selected
                          ? "border-[var(--green)] bg-[var(--green-bg)]"
                          : "border-[var(--rule)] bg-[var(--bg)]",
                      )}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setChoice(option.key)}
                        className="h-auto w-full items-start justify-start whitespace-normal rounded-none border-0 bg-transparent px-3 py-2.5 text-left font-light hover:bg-[rgba(0,0,0,0.02)]"
                      >
                        <RadioMark selected={selected} />
                        <span className="ml-3 flex min-w-0 flex-1 flex-col">
                          <span className="block text-[13px] font-light text-[var(--ink)]">
                            {option.label}
                          </span>
                          <span className="mt-0.5 block break-words text-[11.5px] font-light leading-[1.4] text-[var(--ink-3)]">
                            {option.description}
                          </span>
                        </span>
                      </Button>
                      {showMergeSection ? (
                        <div className="flex flex-col gap-1.5 border-t-[0.5px] border-t-[rgba(0,0,0,0.08)] bg-[var(--bg)] px-3 pb-3 pt-2.5">
                          <p className="m-0 text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
                            Select existing registration to replace
                          </p>
                          {effectiveMatches.length > 0 ? (
                            effectiveMatches.map((match) => (
                              <DuplicateMatchRow
                                key={match.holding_uuid}
                                match={match}
                                selected={
                                  selectedEffectiveMatchUuid ===
                                  match.holding_uuid
                                }
                                onSelect={() =>
                                  setSelectedMatchUuid(match.holding_uuid)
                                }
                              />
                            ))
                          ) : (
                            <p className="m-0 text-[11.5px] font-light leading-[1.45] text-[var(--ink-3)]">
                              Existing registration details are unavailable.
                              Merging will route this submission to the matched
                              record.
                            </p>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <p className="text-[11px] leading-[1.45] text-[var(--ink-3)]">
            {onStructureScreen
              ? "The product will be registered against this existing property - no duplicate property is created."
              : "This decision will be logged for audit purposes."}
          </p>
        </div>

        <DialogFooter className="flex-row items-center justify-between gap-2 border-t-[0.5px] border-t-[var(--rule)] bg-[var(--bg-subtle)] px-6 py-3.5">
          <div className="flex items-center gap-2">
            {existingRecordHref ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  window.open(
                    existingRecordHref,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                className="h-8 rounded-[4px] px-2.5 text-[11px] font-light normal-case tracking-normal text-[var(--ink-2)] hover:text-[var(--ink)]"
              >
                <ExternalLink className="h-3 w-3" />
                View existing record
              </Button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {onStructureScreen ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setScreen("decision");
                  onPrepareHost(null);
                }}
                className="h-8 rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 text-[11px] font-light uppercase tracking-[0.1em] text-[var(--ink-2)]"
              >
                <ArrowLeft className="h-3 w-3" />
                Back
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                onClick={onGoBack}
                className="h-8 rounded-[4px] px-2.5 text-[11px] font-light uppercase tracking-[0.1em] text-[var(--ink-2)] hover:text-[var(--ink)]"
              >
                Go back
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="h-8 rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 text-[11px] font-light uppercase tracking-[0.1em] text-[var(--ink-2)]"
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={
                onStructureScreen
                  ? !canSubmit
                  : !choice ||
                    !selectedOption ||
                    (choice !== "add_new_product" && !canSubmit)
              }
              variant="default"
              onClick={() => {
                // "Continue with this property" advances to the structure
                // path screen; every other decision resolves directly.
                if (!onStructureScreen && choice === "add_new_product") {
                  onPrepareHost(matchedHostUuid);
                  setScreen("structure");
                  return;
                }
                handleResolve();
              }}
              className="h-8 rounded-[4px] px-3.5 text-[11px] font-light uppercase tracking-[0.1em]"
            >
              {onStructureScreen ? (
                <>
                  <Check className="h-3 w-3" />
                  Confirm
                </>
              ) : choice === "merge" ? (
                <>
                  <Check className="h-3 w-3" />
                  Merge · cancel original
                </>
              ) : choice === "confirmed_duplicate" ? (
                "Confirm duplicate"
              ) : choice === "add_new_product" ? (
                "Continue"
              ) : (
                "Choose an option"
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HostReviewIcon({ icon }: { icon?: string | null }) {
  switch (icon) {
    case "car":
      return <Car className="h-3.5 w-3.5" />;
    case "home":
    case "house":
      return <Home className="h-3.5 w-3.5" />;
    case "ship":
    case "boat":
    case "anchor":
      return <Ship className="h-3.5 w-3.5" />;
    default:
      return <Package className="h-3.5 w-3.5" />;
  }
}

const STATUS_WORKFLOW_KEY = "lifecycle_status";

function pickStatusWorkflowState(
  states: WorkflowStateRef[] | undefined,
): WorkflowStateRef | null {
  if (!states?.length) return null;
  return (
    states.find(
      (workflowState) => workflowState.workflow_key === STATUS_WORKFLOW_KEY,
    ) ??
    states.find((workflowState) => workflowState.owns_resource_lifecycle) ??
    null
  );
}

function isHoldingActive(holding: Holding | undefined): boolean {
  if (!holding) return false;
  const statusWorkflowState = pickStatusWorkflowState(holding.workflow_states);
  const statusWorkflowKey =
    statusWorkflowState?.state_key?.toLowerCase() ?? null;
  return statusWorkflowKey === "active" || statusWorkflowKey === "registered";
}

// ── Certificate print helper ────────────────────────────

function buildCertificateRow(
  label: string,
  value: string | null | undefined,
): string {
  if (!value) return "";
  return `
    <tr>
      <td style="padding:8px 0;font-size:11px;color:#9a9a94;text-transform:uppercase;letter-spacing:0.12em;font-weight:300;white-space:nowrap;vertical-align:baseline">${label}</td>
      <td style="padding:8px 0 8px 24px;font-size:13px;color:#1a1a18;font-weight:300;text-align:right;vertical-align:baseline">${value}</td>
    </tr>`;
}

function buildCertificateSection(title: string, rows: string): string {
  if (!rows.trim()) return "";
  return `
    <div style="margin-bottom:24px">
      <div style="font-size:9px;text-transform:uppercase;letter-spacing:0.18em;color:#9a9a94;padding-bottom:10px;border-bottom:0.5px solid rgba(26,26,24,0.15);margin-bottom:4px">${title}</div>
      <table style="width:100%;border-collapse:collapse">${rows}</table>
    </div>`;
}

interface SuccessViewProps {
  reference: string;
  holdingUuid: string;
  holdingStatus?: string | null;
  productName: string;
  customerName: string;
  statusWorkflowState: WorkflowStateRef | null;
  registeredWarranty?: RegistrationResponse["warranty"];
  pendingWarranty?: PendingWarrantySummary | null;
  approvalRequired?: boolean;
  state: WizardState;
  metadata: RegistrationFormMetadata | null;
  warrantyTemplates: WarrantyTemplateSummary[];
  registrationFields: RegistrationField[];
  onReset?: () => void;
  fmtDate: (iso: string) => string;
  fmtCurrency: (n: number) => string;
  orgUuid: string | null;
  orgName: string | null;
  orgLogoUrl: string | null;
  returnTo?: string | null;
  claimSchemaKey?: string | null;
  contractorRoleName?: string;
}

function buildCertificateHtml({
  reference,
  productName,
  customerName,
  state,
  metadata,
  warrantyTemplates,
  registrationFields,
  fmtDate,
  fmtCurrency,
  orgName,
  orgLogoUrl,
  contractorRoleName = "Dealer",
}: SuccessViewProps): string {
  const selectedClass = warrantyTemplates.find(
    (wc) => wc.uuid === state.warrantyTemplateUuid,
  );
  const selectedOption = selectedClass?.options.find(
    (o) => o.uuid === state.warrantyTemplateOptionUuid,
  );
  const optionLabel = selectedOption
    ? (selectedOption as { is_lifetime?: boolean }).is_lifetime
      ? "Lifetime"
      : (selectedOption as { display_name?: string }).display_name ||
        formatDurationFromMonths(
          (selectedOption as { duration_in_months?: number })
            .duration_in_months,
        )
    : null;

  const today = fmtDate(new Date().toISOString());

  // Build host/vehicle rows
  let hostSectionTitle = "Details";
  let hostRows = "";

  if (metadata?.host_template) {
    hostSectionTitle = metadata.host_template.name;
    if (
      metadata.host_requirement === "OPTIONAL" &&
      state.isInstalled === false
    ) {
      hostRows = buildCertificateRow("Installed", "No");
    } else {
      [...metadata.host_template.attributes]
        .sort((a, b) => a.order - b.order)
        .forEach((attr) => {
          const val = state.hostAttributeValues?.[attr.code_name];
          const formattedAddress = addressDisplay(val);
          const displayVal =
            attr.field_type === "ADDRESS" || formattedAddress
              ? formattedAddress
              : val === true
                ? "Yes"
                : val === false
                  ? "No"
                  : val != null
                    ? String(val)
                    : null;
          if (displayVal)
            hostRows += buildCertificateRow(attr.display_name, displayVal);
        });
      // Append host-step RegistrationField values (tenant-defined,
      // generic - no hardcoded code_name).
      (registrationFields ?? [])
        .filter(
          (f) =>
            f.is_active &&
            (f.capture_step === "host" || f.capture_step === "host_context"),
        )
        .forEach((f) => {
          const display = formatRegistrationFieldTextValue(
            f,
            state.registrationFieldValues[f.code_name],
          );
          if (display) hostRows += buildCertificateRow(f.display_name, display);
        });
    }
  }

  // Registration details
  let regRows = "";
  regRows += buildCertificateRow("Product", productName);
  if (selectedClass) {
    regRows += buildCertificateRow(
      "Warranty",
      selectedClass.name + (optionLabel ? ` \u00b7 ${optionLabel}` : ""),
    );
  }
  if (state.purchasePrice) {
    regRows += buildCertificateRow(
      "Price",
      fmtCurrencyStr(state.purchasePrice, fmtCurrency),
    );
  }
  const startDateStr = state.warrantyStartDate;
  if (startDateStr) {
    regRows += buildCertificateRow("Start Date", fmtDate(startDateStr));
  }
  if (selectedOption) {
    if ((selectedOption as { is_lifetime?: boolean }).is_lifetime) {
      regRows += buildCertificateRow("End Date", "Lifetime");
    } else {
      const durMonths = (selectedOption as { duration_in_months?: number })
        .duration_in_months;
      if (startDateStr && durMonths) {
        const start = new Date(startDateStr);
        start.setMonth(start.getMonth() + durMonths);
        regRows += buildCertificateRow(
          "End Date",
          fmtDate(start.toISOString()),
        );
      }
    }
  }
  regRows += buildCertificateRow(contractorRoleName, state.partnershipName);
  regRows += buildCertificateRow("Reference", reference);

  // Customer
  let custRows = "";
  custRows += buildCertificateRow("Name", customerName);
  custRows += buildCertificateRow("Email", customerOwnEmail(state));
  custRows += buildCertificateRow("Phone", state.consumerPhone);
  const addr = addressDisplay(state.address);
  if (addr) custRows += buildCertificateRow("Address", addr);

  // The certificate is a self-contained document, so resolve the tenant's
  // themed fonts (applied to :root by applyTenantTheme) to literal stacks and
  // import their webfonts inline. Font-family goes in the <style> block, not
  // inline style="" attributes, because a stack's double-quoted family names
  // would otherwise break the attribute delimiter.
  const { display: certDisplayFont, sans: certSansFont } =
    resolveDocumentFonts();
  const certFontImport = googleFontImport(certDisplayFont, certSansFont);

  return `<!DOCTYPE html>
<html>
<head>
  <title>Certificate of Registration - ${reference}</title>
  <style>
    ${certFontImport}
    @page { size: A4; margin: 28mm; }
    body { margin: 0; padding: 0; background: #faf9f7; font-family: ${certSansFont}; }
    .cert-display { font-family: ${certDisplayFont}; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div style="max-width:600px;margin:0 auto;padding:48px 0">
    <!-- Header -->
    <div style="display:flex;justify-content:space-between;align-items:center;padding-bottom:32px;border-bottom:0.5px solid rgba(26,26,24,0.15);margin-bottom:40px">
      ${
        orgLogoUrl
          ? `<img src="${orgLogoUrl}" alt="${orgName || ""}" style="max-height:40px;max-width:180px;object-fit:contain" />`
          : `<div class="cert-display" style="font-size:16px;color:#1a1a18">${orgName || "iWarranty"}</div>`
      }
      <div style="text-align:right">
        <div class="cert-display" style="font-size:24px;color:#1a1a18">Certificate of Registration</div>
      </div>
    </div>

    <!-- Hero -->
    <div style="text-align:center;margin-bottom:32px">
      <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.14em;color:#4a4a46;margin-bottom:12px">${productName}</div>
      <div class="cert-display" style="font-size:34px;color:#1a1a18;margin-bottom:8px">${reference}</div>
      <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.10em;color:#9a9a94">${customerName}${state.purchaseDate ? " \u00b7 " + fmtDate(state.purchaseDate) : ""}</div>
    </div>

    <div style="width:100%;height:0.5px;background:#edece9;margin-bottom:24px"></div>

    <!-- Sections -->
    ${buildCertificateSection(hostSectionTitle, hostRows)}
    ${buildCertificateSection("Registration Details", regRows)}
    ${buildCertificateSection("Customer", custRows)}

    <!-- Footer -->
    <div style="margin-top:48px;padding-top:20px;border-top:0.5px solid rgba(26,26,24,0.08);display:flex;justify-content:space-between;align-items:flex-start">
      <div style="font-size:10px;color:#9a9a94;line-height:1.7;max-width:380px">This document is a summary of your registration. Please refer to your full terms and conditions for complete details.</div>
      <div style="text-align:right">
        <div style="font-size:9px;text-transform:uppercase;letter-spacing:0.10em;color:#9a9a94">GENERATED ${today.toUpperCase()}</div>
        <div class="cert-display" style="font-size:12px;color:#9a9a94;margin-top:4px">${orgName || "iWarranty"}</div>
      </div>
    </div>
  </div>
</body>
</html>`;
}

// Card-body placeholder shown while a review card's display config is
// still being fetched. Renders instead of the hardcoded fallback so the
// preview does not flash the wrong values and then swap once the
// per-entity display config lands (SX-229).
function CardConfigLoading() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center gap-2 py-6">
      <Loader2 size={16} className="animate-spin text-[var(--ink-3)]" />
      <span className="text-[12px] text-[var(--ink-3)]">
        {t("common.loading", "Loading...")}
      </span>
    </div>
  );
}

// ── Success view ─────────────────────────────────────────

function SuccessView({
  reference,
  holdingUuid,
  holdingStatus,
  productName,
  customerName,
  statusWorkflowState,
  registeredWarranty,
  pendingWarranty,
  approvalRequired = false,
  state,
  metadata,
  warrantyTemplates,
  registrationFields,
  onReset,
  fmtDate,
  fmtCurrency,
  orgUuid,
  orgName,
  orgLogoUrl,
  returnTo,
  claimSchemaKey,
  contractorRoleName = "Dealer",
}: SuccessViewProps) {
  const navigate = useNavigate();
  const [isDownloadingCertificate, setIsDownloadingCertificate] =
    useState(false);
  const certificateDownloadEnabled = useCertificateDownloadEnabled();
  const { locale, currency } = useFormatting();
  // Tenant-configured warranty card columns (Settings → Warranty
  // Templates → Display tab). When present, the success card renders the
  // configured rows; otherwise we fall back to the hardcoded summary.
  const { data: warrantyEntity, isConfigLoading: warrantyConfigLoading } =
    useEntityTypeByKey("warranty");
  const { data: entityTypes = [], isLoading: entityTypesLoading } =
    useEntityTypes();
  const { t } = useTranslation();
  const warrantyDisplayColumns = registrationCardDisplayColumns(warrantyEntity);
  // The tenant's own word for a warranty. Supagard calls it a Guarantee, and
  // this screen is the first thing a dealer sees after registering.
  const warrantyNoun = warrantyEntity?.label || "Warranty";
  // The noun is only trustworthy once the entity config lands. Until then
  // `warrantyEntity` is undefined and the fallback reads "Warranty", so any
  // copy carrying the noun flashed the platform word and then swapped to the
  // tenant's own (Supagard saw "warranty" -> "guarantee"). Same reason the
  // card body waits on `warrantyCardLoading` below (SX-229).
  const warrantyNounLoading = warrantyConfigLoading;
  const hasConfiguredWarrantyCard = warrantyDisplayColumns.length > 0;

  const buildFallbackCertificate = () =>
    buildCertificateHtml({
      reference,
      holdingUuid,
      productName,
      customerName,
      statusWorkflowState,
      state,
      metadata,
      warrantyTemplates,
      registrationFields,
      fmtDate,
      fmtCurrency,
      orgName,
      orgLogoUrl,
      contractorRoleName,
    });

  const handleDownload = async () => {
    const win = window.open("", "_blank");
    if (!win) {
      toast.error("Allow pop-ups to download the certificate.");
      return;
    }
    win.document.write(
      '<!doctype html><html><body style="font-family:sans-serif;padding:24px">Preparing certificate...</body></html>',
    );
    win.document.close();

    setIsDownloadingCertificate(true);
    let html =
      '<!doctype html><html><body style="font-family:sans-serif;padding:24px">Unable to prepare the certificate. Please try again.</body></html>';

    try {
      html = buildFallbackCertificate();
    } catch {
      toast.error("Unable to prepare the certificate.");
    }

    if (orgUuid && holdingUuid) {
      try {
        const res = await api.post(
          `/organisations/${orgUuid}/certificate-templates/preview/`,
          {
            template_slug: "registration_certificate",
            holding_uuid: holdingUuid,
          },
        );
        html = res.data?.html || html;
      } catch {
        // Fall back to the client-rendered certificate so the action still works.
      }
    }

    win.document.open();
    win.document.write(html);
    win.document.close();
    // The certificate HTML carries no width constraint, so on a wide monitor
    // the on-screen preview stretches edge to edge and reads as broken. Print
    // is already page-constrained, so this tames the screen view only.
    const screenWidth = win.document.createElement("style");
    screenWidth.textContent =
      "@media screen{body{max-width:820px;margin:0 auto;padding:24px}}";
    win.document.head?.appendChild(screenWidth);
    // Wait for fonts/images to load before triggering save-as-PDF dialog
    setTimeout(() => {
      win.print();
      setIsDownloadingCertificate(false);
    }, 600);
  };

  const statusWorkflowKey =
    statusWorkflowState?.state_key?.toLowerCase() ?? null;
  const legacyHoldingStatus = holdingStatus?.toLowerCase() ?? null;
  const isWorkflowActive =
    statusWorkflowKey === "active" || statusWorkflowKey === "registered";
  const isLegacyActive =
    !statusWorkflowState &&
    (legacyHoldingStatus === "active" || legacyHoldingStatus === "registered");
  const isRegistrationActive = isWorkflowActive || isLegacyActive;
  const isWorkflowDraft =
    statusWorkflowKey === "draft" ||
    (!statusWorkflowState && legacyHoldingStatus === "draft");
  // Look up the option the user picked so the Duration row can render a
  // clean "6 months" / "3 years" / "Lifetime" - the warranty payload's
  // ``option_display_name`` is "<template name> - <duration>" and reads as
  // duplicated junk when used as the Duration value on its own.
  const selectedClassForDuration = warrantyTemplates.find(
    (wc) => wc.uuid === state.warrantyTemplateUuid,
  );
  const selectedOptionForDuration = selectedClassForDuration?.options.find(
    (option) => option.uuid === state.warrantyTemplateOptionUuid,
  );
  const warrantyCardSource: {
    uuid?: string;
    class_name: string;
    option_display_name: string;
    status: string;
    active_from: string | null;
    expires_at: string | null;
    duration_in_months: number | null;
    is_lifetime: boolean;
    attribute_values?: NonNullable<
      RegistrationResponse["warranty"]
    >["attribute_values"];
  } | null = registeredWarranty
    ? {
        uuid: registeredWarranty.uuid,
        class_name: registeredWarranty.class_name,
        option_display_name: registeredWarranty.option_display_name,
        status: registeredWarranty.status,
        active_from: registeredWarranty.active_from,
        expires_at: registeredWarranty.expires_at,
        duration_in_months:
          selectedOptionForDuration?.duration_in_months ?? null,
        is_lifetime: selectedOptionForDuration?.is_lifetime ?? false,
        attribute_values: registeredWarranty.attribute_values,
      }
    : pendingWarranty
      ? {
          class_name: pendingWarranty.class_name,
          option_display_name: pendingWarranty.option_display_name,
          status: isRegistrationActive ? "Active" : pendingWarranty.status,
          active_from: pendingWarranty.active_from,
          expires_at: null,
          duration_in_months: pendingWarranty.duration_in_months,
          is_lifetime: pendingWarranty.is_lifetime,
        }
      : null;
  const warrantyDurationLabel = warrantyCardSource
    ? warrantyCardSource.is_lifetime
      ? "Lifetime"
      : formatDurationFromMonths(warrantyCardSource.duration_in_months) ||
        warrantyCardSource.option_display_name
    : "";
  const showWarrantyCard = warrantyCardSource !== null;
  // Fetch the full holding (with relationships) only when the warranty
  // entity has display columns configured - the configured renderer
  // needs the full graph that the wizard response does not include.
  const { data: holdingDetail, isLoading: holdingDetailLoading } = useHolding(
    hasConfiguredWarrantyCard && showWarrantyCard ? holdingUuid : null,
  );
  // Hold the warranty card in a loading state until we know whether it has
  // configured display columns AND (when it does) the full holding graph
  // the configured renderer needs has landed. Prevents the hardcoded
  // fallback from flashing and then swapping to the configured rows
  // (SX-229).
  const warrantyCardLoading =
    warrantyConfigLoading ||
    entityTypesLoading ||
    (hasConfiguredWarrantyCard && showWarrantyCard && holdingDetailLoading);
  const warrantyStatusLabel = warrantyCardSource
    ? formatWarrantyStatusLabel(warrantyCardSource.status)
    : undefined;
  const configuredWarrantyItems =
    hasConfiguredWarrantyCard && holdingDetail
      ? buildRegistrationConfiguredFieldItems({
          holding: holdingDetail,
          columns: warrantyDisplayColumns,
          statusWorkflowKey: statusWorkflowKey ?? undefined,
          entityTypes,
          locale,
          currency,
        })
          // Hide-when-empty, matching the card convention (see
          // buildRegistrationConfiguredCardItems) and the fallback path in this
          // same card. The warranty entity's registration_card columns are
          // tenant-wide - every warranty EAV attribute across every template -
          // so a solar "Standard" warranty would otherwise render the roofing
          // template's attributes (TRU Protection Years, Algae Resistance, Wind
          // Warranty, Workmanship, etc.) as empty "-" rows. Those attributes
          // simply don't belong to the registered warranty; drop the empties so
          // the card shows only the fields the warranty actually carries.
          .filter((item) => !item.muted)
      : [];
  const useConfiguredWarrantyCard = configuredWarrantyItems.length > 0;
  const showCertificateAction =
    isRegistrationActive && certificateDownloadEnabled;
  const statusMessage = isRegistrationActive ? (
    <>
      {t(
        "registration.success.completed",
        "Registration completed successfully.",
      )}
      {!warrantyNounLoading && (
        <>
          <br />
          {t(
            "registration.success.readyToView",
            "The certificate and {{warrantyNounLower}} card are ready to view.",
            { warrantyNounLower: warrantyNoun.toLowerCase() },
          )}
        </>
      )}
    </>
  ) : approvalRequired || isWorkflowDraft ? (
    <>
      Registration saved as draft.
      <br />
      The confirmation email will be sent once it becomes active.
    </>
  ) : statusWorkflowState ? (
    <>
      Registration moved to {statusWorkflowState.state_label}.<br />
      The confirmation email will be sent once it becomes active.
    </>
  ) : (
    <>
      Registration submitted successfully.
      <br />
      The confirmation email will be sent once it becomes active.
    </>
  );

  return (
    <div className="panel-arrive mx-auto max-w-[780px] py-[60px] text-center">
      {/* Shield icon */}
      <div className="mb-4">
        <ShieldCheck
          className={cn(
            "mx-auto h-8 w-8",
            isRegistrationActive
              ? "text-[var(--green)]"
              : "text-[var(--ink-3)]",
          )}
        />
      </div>
      <h2 className="mb-3 font-display text-[48px] tracking-[-0.01em] text-[var(--ink)]">
        {reference}
      </h2>
      <div className="mx-auto mb-5 h-[0.5px] w-12 bg-[var(--rule-strong)]" />
      <p className="mb-8 text-[13px] leading-[1.7] tracking-[0.04em] text-[var(--ink-3)]">
        {statusMessage}
      </p>
      <p className="mb-8 text-[12px] text-[var(--ink-3)]">
        {productName} &middot; {customerName}
      </p>
      {showWarrantyCard && warrantyCardSource && !warrantyNounLoading && (
        <div className="mx-auto mb-8 max-w-[520px] text-left">
          <DetailCard
            title={
              isRegistrationActive
                ? t(
                    "registration.success.registeredWarranty",
                    "Registered {{warrantyNounLower}}",
                    { warrantyNounLower: warrantyNoun.toLowerCase() },
                  )
                : t(
                    "registration.success.selectedWarranty",
                    "Selected {{warrantyNounLower}}",
                    { warrantyNounLower: warrantyNoun.toLowerCase() },
                  )
            }
            icon={<ShieldCheck className="h-3.5 w-3.5" />}
            collapsible={false}
          >
            {warrantyCardLoading ? (
              <CardConfigLoading />
            ) : useConfiguredWarrantyCard ? (
              <RegistrationConfiguredFieldRows
                items={configuredWarrantyItems}
              />
            ) : (
              <>
                <FieldRow label="Class" value={warrantyCardSource.class_name} />
                <FieldRow label="Duration" value={warrantyDurationLabel} />
                <FieldRow
                  label="Status"
                  value={
                    warrantyStatusLabel ? (
                      <StatusBadge status={warrantyStatusLabel} />
                    ) : undefined
                  }
                />
                {warrantyCardSource.active_from && (
                  <FieldRow
                    label="Active from"
                    value={fmtDate(warrantyCardSource.active_from)}
                  />
                )}
                {warrantyCardSource.expires_at && (
                  <FieldRow
                    label="Expires"
                    value={fmtDate(warrantyCardSource.expires_at)}
                  />
                )}
                {(warrantyCardSource.attribute_values ?? []).map(
                  (attribute) => {
                    const displayValue = formatWarrantyAttributeValue(
                      attribute.field_type,
                      attribute.value,
                      fmtDate,
                    );
                    if (!displayValue) return null;
                    return (
                      <FieldRow
                        key={`${warrantyCardSource.uuid ?? "pending"}-${attribute.code_name}`}
                        label={attribute.display_name}
                        value={displayValue}
                      />
                    );
                  },
                )}
              </>
            )}
          </DetailCard>
        </div>
      )}
      <div className="flex justify-center gap-3">
        {returnTo && holdingUuid && (
          <Button
            type="button"
            variant="default"
            onClick={() => {
              // ``returnTo`` may already carry a query string (e.g. an HOA
              // claim passes ?claim_type=hoa). Append params with the correct
              // separator so the existing query isn't clobbered.
              const sep = returnTo.includes("?") ? "&" : "?";
              const schema = claimSchemaKey
                ? `&claimSchemaKey=${claimSchemaKey}`
                : "";
              navigate(`${returnTo}${sep}holding=${holdingUuid}${schema}`);
            }}
            className="h-9 cursor-pointer rounded-[6px] bg-[var(--green)] px-5 text-[10px] font-normal uppercase tracking-[0.12em] text-white"
          >
            Continue to Claim
          </Button>
        )}
        {showCertificateAction && (
          <Button
            type="button"
            variant="outline"
            onClick={handleDownload}
            disabled={isDownloadingCertificate}
            className="h-9 cursor-pointer border-[0.5px] border-[var(--rule)] bg-transparent px-5 text-[10px] font-light uppercase tracking-[0.12em] text-[var(--ink)] hover:border-[var(--ink-3)]"
          >
            {isDownloadingCertificate ? "Preparing..." : "Download certificate"}
          </Button>
        )}
        {holdingUuid && (
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/registrations/${holdingUuid}`)}
            className="h-9 cursor-pointer border-[0.5px] border-[var(--rule)] bg-transparent px-5 text-[10px] font-light uppercase tracking-[0.12em] text-[var(--ink)] hover:border-[var(--ink-3)]"
          >
            View registration
          </Button>
        )}
        {!returnTo && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (onReset) onReset();
            }}
            className="h-9 cursor-pointer border-[0.5px] border-[var(--rule)] bg-transparent px-5 text-[10px] font-light uppercase tracking-[0.12em] text-[var(--ink)] hover:border-[var(--ink-3)]"
          >
            New registration
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Component ────────────────────────────────────────────

export function StepReview({
  state,
  update,
  back,
  metadata,
  warrantyTemplates,
  onSubmit,
  isSubmitting,
  onReset,
  submissionWarningHits = [],
  submissionBlockedMessage = null,
  submissionApprovalHits = [],
  segments = [],
  returnTo,
  claimSchemaKey,
}: StepReviewProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatCurrency, formatDateLong, locale, currency } = useFormatting();
  const { orgUuid, orgName, orgLogoUrl } = useOrg();
  const { data: registrationFields } = useRegistrationFields();
  const [consentInvalid, setConsentInvalid] = useState<string[]>([]);
  const { data: externalRoles = [] } = useExternalRoles();
  // Profile of the contractor assigned on the property step, for its own
  // review card. Null until one is picked, which hides the card.
  const { data: reviewPartner } = usePartnershipDetail(
    state.partnershipUuid ?? null,
  );
  const contractorRoleName = externalRoles[0]?.name ?? "Dealer";
  const { data: hostEntity, isConfigLoading: hostConfigLoading } =
    useEntityTypeByKey("host");
  const { data: holdingEntity, isConfigLoading: holdingConfigLoading } =
    useEntityTypeByKey("holding");
  const { data: entityTypes = [], isLoading: entityTypesLoading } =
    useEntityTypes();
  // The review cards render from per-entity display config. Until that
  // config has landed, the configured-rows arrays are empty and we would
  // otherwise fall into the hardcoded fallback branch - which is exactly
  // the flash SX-229 reports. Gate each card on its own config-loading
  // signal and show a loading indicator instead.
  const hostCardLoading = hostConfigLoading || entityTypesLoading;
  const holdingCardLoading = holdingConfigLoading || entityTypesLoading;
  const duplicateAddressIssue =
    state.registrationIssues.find(
      (issue) => issue.kind === "duplicate_address" && issue.status === "open",
    ) ?? null;
  const [resolvingIssueId, setResolvingIssueId] = useState<string | null>(null);
  // Tenant-defined RegistrationFields with a value to surface on review.
  // Generic - driven entirely by what the tenant's seed / admin UI
  // configured. No code_name is hardcoded.
  const reviewRegFields = (registrationFields ?? []).filter((f) => {
    if (!f.is_active) return false;
    // Disclaimer / terms fields are acknowledgement surfaces, not data rows.
    // They render in the dedicated acknowledgement panel at the bottom of
    // review (below), so keep them out of the read-only detail sections to
    // avoid a duplicate "Yes" row.
    if (isDisclaimerRegistrationField(f)) return false;
    const display = formatRegistrationFieldValue(
      f,
      state.registrationFieldValues[f.code_name],
    );
    return display !== null;
  });

  // ── Catch-all: disclaimer / terms acknowledgements on Review ──
  //
  // Any active RegistrationField marked `display_as: 'disclaimer'` is surfaced
  // here, at the very bottom of Review, right above the submit button - the
  // last thing acknowledged before registering. Generic and config-driven: a
  // tenant opts a consent field in simply by marking it a disclaimer (no
  // tenant.slug, no hardcoded code_name). Submission is blocked until every
  // required disclaimer is ticked.
  const disclaimerFormValues = useMemo<Record<string, unknown>>(
    () => ({
      ...state.hostAttributeValues,
      ...state.registrationFieldValues,
      // Mirror new-registration-page's combinedFormValues so visible_when /
      // required_when conditions keyed on the warranty template resolve.
      warrantyTemplateUuid: state.warrantyTemplateUuid,
    }),
    [
      state.hostAttributeValues,
      state.registrationFieldValues,
      state.warrantyTemplateUuid,
    ],
  );
  const reviewDisclaimerFields = useMemo(
    () =>
      (registrationFields ?? []).filter(
        (f) =>
          f.is_active &&
          isDisclaimerRegistrationField(f) &&
          isFieldVisible(
            f.configuration_data as Record<string, unknown> | undefined,
            disclaimerFormValues,
          ),
      ),
    [registrationFields, disclaimerFormValues],
  );
  const unmetDisclaimers = useMemo(
    () =>
      reviewDisclaimerFields.filter((f) => {
        const cd = f.configuration_data as Record<string, unknown> | undefined;
        const required =
          f.required || isConditionallyRequired(cd, disclaimerFormValues);
        if (!required) return false;
        return !isRequiredValueProvided(
          { field_type: f.field_type, configuration_data: cd },
          disclaimerFormValues[f.code_name],
        );
      }),
    [reviewDisclaimerFields, disclaimerFormValues],
  );
  const [disclaimerAttempted, setDisclaimerAttempted] = useState(false);
  const handleDisclaimerChange = (next: Record<string, unknown>) =>
    update({
      registrationFieldValues: {
        ...state.registrationFieldValues,
        ...next,
      },
    });

  const [successData, setSuccessData] = useState<{
    reference: string;
    holdingUuid: string;
    holdingStatus?: string | null;
    productName: string;
    customerName: string;
    statusWorkflowState: WorkflowStateRef | null;
    registeredWarranty: RegistrationResponse["warranty"];
    pendingWarranty: PendingWarrantySummary | null;
    approvalRequired: boolean;
  } | null>(null);
  const [warningConfirmationOpen, setWarningConfirmationOpen] = useState(false);
  const [warningConfirmed, setWarningConfirmed] = useState(false);
  const [cancelledConfirmationOpen, setCancelledConfirmationOpen] =
    useState(false);
  const [cancelledConfirmed, setCancelledConfirmed] = useState(false);
  const [approvalConfirmationOpen, setApprovalConfirmationOpen] =
    useState(false);
  // Remember which specific approval-rule hits the user has already
  // consented to. Storing the set (instead of a boolean) means a user
  // who edits a field and trips additional approval rules is re-prompted,
  // without resetting state inside an effect.
  const [confirmedApprovalHitsKey, setConfirmedApprovalHitsKey] = useState<
    string | null
  >(null);
  const approvalHitsKey = useMemo(
    () =>
      submissionApprovalHits
        .map((hit) => hit.rule_uuid)
        .sort()
        .join("|"),
    [submissionApprovalHits],
  );
  const approvalConfirmed =
    approvalHitsKey !== "" && confirmedApprovalHitsKey === approvalHitsKey;

  const goToStep = (step: number) => update({ step });

  const editStepFor = (section: ReviewSectionKey) =>
    stepIndexForSection(segments, section);

  const selectedClass = warrantyTemplates.find(
    (wc) => wc.uuid === state.warrantyTemplateUuid,
  );
  const selectedWarrantyOption = selectedClass?.options.find(
    (o) => o.uuid === state.warrantyTemplateOptionUuid,
  );
  const selectedWarrantyDuration = selectedWarrantyOption
    ? (selectedWarrantyOption as { is_lifetime?: boolean }).is_lifetime
      ? "Lifetime"
      : (selectedWarrantyOption as { display_name?: string }).display_name ||
        formatDurationFromMonths(
          (selectedWarrantyOption as { duration_in_months?: number })
            .duration_in_months,
        )
    : null;
  const customerName = [state.consumerFirstName, state.consumerLastName]
    .filter(Boolean)
    .join(" ");
  const address = addressDisplay(state.address);
  const mergeReplacementMatch =
    state.duplicateAddressResolution?.decision === "merge"
      ? state.duplicateAddressResolution.resolutionContext?.selectedMatch
      : null;
  const rejectedDuplicateResolution =
    state.duplicateAddressResolution?.decision === "reject"
      ? state.duplicateAddressResolution
      : null;
  const { data: replacementHolding } = useHolding(
    mergeReplacementMatch?.holding_uuid ?? null,
  );
  const replacementConsumer = replacementHolding?.consumer_account ?? null;
  const replacementCustomerName =
    consumerDisplayName(replacementConsumer) ||
    mergeReplacementMatch?.owner_name ||
    mergeReplacementMatch?.owner_email ||
    "";
  const displayCustomerName =
    customerName || (mergeReplacementMatch ? replacementCustomerName : "");
  const displayCustomerEmail =
    (mergeReplacementMatch
      ? replacementConsumer?.email || mergeReplacementMatch.owner_email
      : customerOwnEmail(state)) || "";
  const registeredByEmail =
    !mergeReplacementMatch && state.consumerEmailIsOwners === false
      ? state.consumerEmail
      : "";
  const displayCustomerPhone =
    (mergeReplacementMatch
      ? replacementConsumer?.phone_number
      : state.consumerPhone) || "";
  const propertyAddress = formatPropertyAddressFromWizardContext(
    state,
    metadata,
    registrationFields,
  );
  // Consumer address: explicit mailing address when set, otherwise fall back
  // to the property/installation address (the homeowner typically lives at
  // the same address where the product is installed).
  // When mailingSameAsInstallation is ticked, state.address is null;
  // read the host attribute value directly as a reliable fallback before
  // falling through to formatPropertyAddressFromWizardContext.
  const hostAddressFallback = state.mailingSameAsInstallation
    ? addressDisplay(state.hostAttributeValues?.["address"]) || propertyAddress
    : propertyAddress;
  const displayCustomerAddress = mergeReplacementMatch
    ? formatMailingAddress(replacementHolding?.mailing_address) ||
      address ||
      hostAddressFallback
    : address || hostAddressFallback;
  const reviewDuplicateMatches =
    duplicateAddressIssue?.hits.flatMap((hit) => hit.duplicate_matches ?? []) ??
    [];
  const selectedProduct = state.selectedProduct;
  const productDisplayName =
    selectedProduct?.parent_product?.cached_short_name ||
    (typeof selectedProduct?.product_data?.name === "string"
      ? selectedProduct.product_data.name
      : null) ||
    selectedProduct?.cached_short_name;
  const productVariantDisplayName =
    selectedProduct?.product_type === "VARIANT"
      ? selectedProduct.cached_short_name
      : null;
  // ── Default template-attribute fallback (no card display config) ──
  // When the tenant has authored no product/warranty card display config, the
  // review cards fall back to the template's own attributes. Fetch the full
  // product and warranty template so their attribute values are available;
  // both lists are filtered to non-blank so an attribute that was never
  // captured is not shown as a blank row (mirrors "if not collected, don't
  // display").
  const { data: productDetail } = useProduct(selectedProduct?.uuid ?? null);
  const { data: warrantyTemplateDetail } = useWarrantyTemplate(
    state.warrantyTemplateUuid ?? null,
  );
  const productTemplateAttrRows = useMemo(
    () =>
      (productDetail?.attribute_values ?? []).flatMap((attr) => {
        if (isDisplayEmpty(attr.value)) return [];
        const field = defineField({
          code_name: attr.code_name,
          display_name: attr.display_name,
          field_type: (attr.field_type ?? "TEXT") as FieldType,
        });
        return [
          {
            key: attr.code_name,
            label: attr.display_name,
            value: formatDisplayValue(field, attr.value, { locale, currency }),
          },
        ];
      }),
    [productDetail, locale, currency],
  );
  const warrantyTemplateAttrRows = useMemo(() => {
    if (!warrantyTemplateDetail) return [];
    const option = warrantyTemplateDetail.options?.find(
      (o) => o.uuid === state.warrantyTemplateOptionUuid,
    );
    const overrides = option?.attribute_overrides ?? {};
    const defaults = new Map(
      (warrantyTemplateDetail.attribute_values ?? []).map((av) => [
        av.code_name,
        av.value,
      ]),
    );
    return [...(warrantyTemplateDetail.attributes ?? [])]
      .sort((a, b) => a.order - b.order)
      .flatMap((attr) => {
        const raw =
          attr.code_name in overrides
            ? overrides[attr.code_name]
            : defaults.get(attr.code_name);
        if (isDisplayEmpty(raw)) return [];
        const field = defineField({
          code_name: attr.code_name,
          display_name: attr.display_name,
          field_type: attr.field_type as FieldType,
          configuration_data: attr.configuration_data,
        });
        return [
          {
            key: attr.code_name,
            label: attr.display_name,
            value: formatDisplayValue(field, raw, { locale, currency }),
          },
        ];
      });
  }, [
    warrantyTemplateDetail,
    state.warrantyTemplateOptionUuid,
    locale,
    currency,
  ]);
  const installationDateRaw =
    typeof state.earlyAttributes?.installation_date === "string" &&
    state.earlyAttributes.installation_date
      ? state.earlyAttributes.installation_date
      : undefined;
  const installationDate = installationDateRaw
    ? formatDateLong(installationDateRaw)
    : undefined;
  // WPS-114: label the install date from the holding attribute's own
  // display_name (tenant-configured, e.g. "Start/Installation Date") so the
  // review row stays in lockstep with the capture step and detail page.
  const installationDateLabel =
    holdingEntity?.attributes?.find(
      (attr) => attr.code_name === "installation_date",
    )?.display_name || t("wizard.review.installationDate", "Installation date");
  // The warranty starts on the installation date unless the operator entered
  // an explicit warranty start (mirrors the submit cascade). Shown on the
  // review so the start date the warranty will carry is visible before submit.
  const warrantyStartLabel =
    metadata?.transaction_template?.attributes?.find(
      (attr) => attr.code_name === "warranty_start_date",
    )?.display_name ||
    t("wizard.review.warrantyStartDate", "Warranty start date");
  const warrantyStartDisplay = state.warrantyStartDate
    ? formatDateLong(state.warrantyStartDate)
    : installationDate;
  const subcontractorName =
    typeof state.earlyAttributes?.subcontractor_name === "string" &&
    state.earlyAttributes.subcontractor_name
      ? state.earlyAttributes.subcontractor_name
      : undefined;
  const installedComponentsField = metadata?.fields.find(
    (field) =>
      field.code_name === "installed_components" ||
      field.display_name.toLowerCase() === "installed components",
  );
  const installedComponents = installedComponentsField
    ? formatProductFieldValue(
        installedComponentsField,
        state.dynamicAttributes[installedComponentsField.code_name],
        { locale, currency },
      )
    : null;
  const configuredHostReviewRows = registrationCardDisplayColumns(
    hostEntity,
  ).map((column) => {
    const value = resolveWizardRegistrationCardValue({
      state,
      selectedClass,
      column,
    });
    return {
      key: column.id,
      label: displayColumnLabel(column),
      value: formatWizardDisplayColumnValue({
        value,
        column,
        entityTypes,
        metadata,
        locale,
        currency,
      }),
    };
  });
  const holdingReviewColumns = registrationCardDisplayColumns(holdingEntity);
  const configuredProductReviewRows = holdingReviewColumns
    // The product name renders as a dedicated always-present row below; drop a
    // configured column that resolves to the same value so it isn't duplicated.
    .filter((column) => !columnTargetsProductName(column))
    .map((column) => {
      const value = resolveWizardRegistrationCardValue({
        state,
        selectedClass,
        column,
      });
      return {
        key: column.id,
        label: displayColumnLabel(column),
        value: formatWizardDisplayColumnValue({
          value,
          column,
          entityTypes,
          metadata,
          locale,
          currency,
        }),
      };
    });

  // Purchase review rows: prefer purchase_card; fall back to transaction_attribute: prefixed
  // columns from registration_card for configs saved before the section was split.
  const purchaseReviewColumns = (
    transactionCardDisplayColumns(holdingEntity).length > 0
      ? transactionCardDisplayColumns(holdingEntity)
      : holdingReviewColumns.filter(isTransactionDisplayColumn)
  )
    // WPS-114: install date + warranty start get dedicated, always-resolved
    // rows below (from captured wizard state), so drop any configured columns
    // for them to avoid an empty duplicate row.
    .filter(
      (column) =>
        !WPS114_EXPLICIT_PURCHASE_CODES.has(column.attribute_code_name),
    );
  // "If not collected, don't display": the purchase step renders only the
  // transaction-template fields that survive its gate (configuration_data
  // hidden / DataCaptureConfig / visible_for_warranty_types) and mirrors that
  // exact surviving set into `state.transactionData` - one key per rendered
  // field, kept even when the user left it blank. Reuse that same set here so
  // the card never shows a column for a field the step never collected, while
  // still showing collected-but-blank fields. Only gate when a transaction
  // template drives the step; with no template there is no gate, so the
  // columns pass through unchanged.
  const hasTransactionTemplate =
    (metadata?.transaction_template?.attributes?.length ?? 0) > 0;
  const collectedTransactionCodes = new Set(
    Object.keys(state.transactionData ?? {}),
  );
  const gatedPurchaseReviewColumns = hasTransactionTemplate
    ? purchaseReviewColumns.filter((column) =>
        collectedTransactionCodes.has(column.attribute_code_name),
      )
    : purchaseReviewColumns;
  const sortedTransactionAttrs = useMemo(
    () =>
      [...(metadata?.transaction_template?.attributes ?? [])].sort(
        (a, b) => a.order - b.order,
      ),
    [metadata?.transaction_template?.attributes],
  );
  const configuredPurchaseReviewRows = gatedPurchaseReviewColumns.map(
    (column) => {
      const value = resolveWizardRegistrationCardValue({
        state,
        selectedClass,
        column,
      });
      return {
        key: column.id,
        label: displayColumnLabel(column),
        value: formatWizardDisplayColumnValue({
          value,
          column,
          entityTypes,
          metadata,
          locale,
          currency,
        }),
      };
    },
  );
  const reviewSectionKeys = reviewSectionsForSegments(segments);
  // The assigned contractor gets its own review card, directly after the
  // property it was captured with. Before this the review screen carried only
  // the contractor's name, as one row inside the Purchase card (WPS-163).
  if (state.partnershipUuid && !reviewSectionKeys.includes("contractor")) {
    const afterHost = reviewSectionKeys.indexOf("host");
    if (afterHost >= 0) {
      reviewSectionKeys.splice(afterHost + 1, 0, "contractor");
    } else {
      reviewSectionKeys.unshift("contractor");
    }
  }
  // Always show the customer section in review even when the customer
  // step was skipped (e.g. pre-filled from the property owner). The
  // data is in the wizard state and the user should be able to verify it.
  if (!reviewSectionKeys.includes("customer")) {
    // Insert before the last section (review) so it appears in a natural
    // position - after purchase, before registration_details.
    const insertAt = reviewSectionKeys.indexOf("registration_details");
    if (insertAt >= 0) {
      reviewSectionKeys.splice(insertAt, 0, "customer");
    } else {
      reviewSectionKeys.push("customer");
    }
  }
  const reviewFieldsForSection = (section: ReviewSectionKey) => {
    const captureSteps: Record<ReviewSectionKey, string[]> = {
      host: ["host", "host_context", "host_product"],
      // The contractor card renders the assigned partner's own profile, not
      // registration fields, so it claims no capture step.
      contractor: [],
      product: ["product", "host_product"],
      warranty: ["warranty"],
      purchase: ["purchase", "purchase_customer"],
      customer: ["customer", "purchase_customer"],
      registration_details: ["registration_details"],
    };
    return reviewRegFields.filter((field) => {
      if (!field.capture_step) return section === "registration_details";
      return captureSteps[section].includes(field.capture_step);
    });
  };

  const finalizeSuccess = (response: RegistrationResponse) => {
    // Show the canonical registration reference the record + confirmation
    // email use, not a throwaway random number. Keeps the success screen,
    // the holding detail page, and the email aligned on one identifier.
    const ref = formatRegistrationReference(response.holding);
    const registeredWarranty =
      response.warranty ??
      response.holding.warranties?.find(
        (warranty) => warranty.status?.toLowerCase() === "active",
      ) ??
      response.holding.warranties?.[0] ??
      null;
    setSuccessData({
      reference: ref,
      holdingUuid: response.holding.uuid,
      holdingStatus: response.holding.status ?? null,
      productName:
        response.holding.product.cached_short_name ||
        state.selectedProduct?.cached_short_name ||
        "",
      customerName,
      statusWorkflowState: pickStatusWorkflowState(
        response.holding.workflow_states,
      ),
      registeredWarranty,
      pendingWarranty: response.holding.pending_warranty_summary ?? null,
      approvalRequired: Boolean(response.approval_required),
    });
    update({ registrationComplete: true });
  };

  const submitRegistration = async () => {
    if (duplicateAddressIssue) {
      setResolvingIssueId(duplicateAddressIssue.id);
      return;
    }
    try {
      const result = await onSubmit();
      if (result.type === "submitted") {
        finalizeSuccess(result.response);
      }
    } catch {
      // Error toast handled by parent
    }
  };

  const handleConfirm = async () => {
    if (submissionBlockedMessage) {
      return;
    }
    // Block submission until every required disclaimer / terms
    // acknowledgement is ticked (the last gate before registering).
    if (unmetDisclaimers.length > 0) {
      setDisclaimerAttempted(true);
      toast.error(
        unmetDisclaimers.length === 1
          ? `${unmetDisclaimers[0].display_name} is required`
          : "Please acknowledge all required terms before submitting",
      );
      return;
    }
    const missingConsents = missingRequiredConsents(metadata?.consents, state.consents);
    if (missingConsents.length > 0) {
      setConsentInvalid(missingConsents);
      return;
    }
    if (rejectedDuplicateResolution && !cancelledConfirmed) {
      setCancelledConfirmationOpen(true);
      return;
    }
    if (submissionApprovalHits.length > 0 && !approvalConfirmed) {
      setApprovalConfirmationOpen(true);
      return;
    }
    if (submissionWarningHits.length > 0 && !warningConfirmed) {
      setWarningConfirmationOpen(true);
      return;
    }
    await submitRegistration();
  };

  const handleConfirmCancelledRegistration = async () => {
    setCancelledConfirmed(true);
    setCancelledConfirmationOpen(false);
    await submitRegistration();
  };

  const handleConfirmWarnings = async () => {
    setWarningConfirmed(true);
    setWarningConfirmationOpen(false);
    await submitRegistration();
  };

  const handleConfirmApproval = async () => {
    setConfirmedApprovalHitsKey(approvalHitsKey);
    // Approval rules already commit the registration to DRAFT and raise a
    // workflow issue, so any WARN rule's "save as draft" confirmation is
    // redundant - auto-acknowledge it alongside the approval decision.
    setWarningConfirmed(true);
    setApprovalConfirmationOpen(false);
    await submitRegistration();
  };

  const handleResolveDuplicateIssue = (
    decision: DuplicateAddressDecision,
    decisionLabel: string,
    notes: string,
    selectedHoldingUuid?: string,
  ) => {
    if (!duplicateAddressIssue) return;
    if (decision === "add_new_product") {
      update({
        registrationIssues: state.registrationIssues.filter(
          (issue) => issue.id !== duplicateAddressIssue.id,
        ),
      });
      setResolvingIssueId(null);
      return;
    }
    if (decision === "confirmed_duplicate") {
      update({
        registrationIssues: state.registrationIssues.filter(
          (issue) => issue.id !== duplicateAddressIssue.id,
        ),
      });
      setResolvingIssueId(null);
      navigate("/registrations");
      return;
    }
    update({
      registrationIssues: state.registrationIssues.map((issue) =>
        issue.id === duplicateAddressIssue.id
          ? {
              ...issue,
              status: "resolved",
              decision,
              decisionLabel,
              notes,
              selectedHoldingUuid,
              resolvedAt: new Date().toISOString(),
            }
          : issue,
      ),
    });
    setResolvingIssueId(null);
  };

  if (successData) {
    return (
      <SuccessView
        {...successData}
        state={state}
        metadata={metadata}
        warrantyTemplates={warrantyTemplates}
        registrationFields={registrationFields ?? []}
        onReset={onReset}
        fmtDate={formatDateLong}
        fmtCurrency={formatCurrency}
        orgUuid={orgUuid}
        orgName={orgName}
        orgLogoUrl={orgLogoUrl}
        returnTo={returnTo}
        claimSchemaKey={claimSchemaKey}
        contractorRoleName={contractorRoleName}
      />
    );
  }

  return (
    <div>
      {/* Grid - max 2 columns; cards wrap to additional rows as needed. */}
      <div className="panel-arrive grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
        {reviewSectionKeys.map((section) => {
          if (section === "host") {
            if (!metadata?.host_template) return null;
            return (
              <DetailCard
                key="host"
                title={
                  state.selectedExistingHostUuid
                    ? `${metadata.host_template.name} (existing)`
                    : metadata.host_template.name
                }
                icon={<HostReviewIcon icon={metadata.host_template.icon} />}
                collapsible={false}
                trailing={
                  <ReviewEditButton
                    onClick={() => goToStep(editStepFor("host"))}
                  />
                }
              >
                {duplicateAddressIssue ? (
                  <DuplicateAddressReviewWarning
                    issue={duplicateAddressIssue}
                    onResolve={() =>
                      setResolvingIssueId(duplicateAddressIssue.id)
                    }
                  />
                ) : null}
                {metadata.host_requirement === "OPTIONAL" &&
                state.isInstalled === false ? (
                  <FieldRow label="Installed" value="No" />
                ) : hostCardLoading ? (
                  <CardConfigLoading />
                ) : configuredHostReviewRows.length > 0 ? (
                  configuredHostReviewRows.map((row) => (
                    <FieldRow
                      key={row.key}
                      label={row.label}
                      value={row.value}
                    />
                  ))
                ) : (
                  <>
                    {[...metadata.host_template.attributes]
                      .sort((a, b) => a.order - b.order)
                      .map((attr) => {
                        const val = state.hostAttributeValues?.[attr.code_name];
                        const formattedAddress = addressDisplay(val);
                        const displayVal =
                          attr.field_type === "ADDRESS" || formattedAddress
                            ? formattedAddress
                            : val === true
                              ? "Yes"
                              : val === false
                                ? "No"
                                : val != null
                                  ? normaliseDetailCardDisplayValue(val)
                                  : null;
                        if (!displayVal) return null;
                        return (
                          <FieldRow
                            key={attr.uuid}
                            label={attr.display_name}
                            value={displayVal}
                          />
                        );
                      })}
                    {reviewFieldsForSection("host").map((f) => (
                      <FieldRow
                        key={f.uuid}
                        label={f.display_name}
                        value={formatRegistrationFieldValue(
                          f,
                          state.registrationFieldValues[f.code_name],
                          { locale, currency },
                        )}
                      />
                    ))}
                  </>
                )}
              </DetailCard>
            );
          }

          if (section === "contractor") {
            if (!reviewPartner) return null;
            return (
              <DetailCard
                key="contractor"
                title={reviewPartner.role_name}
                icon={<HardHat className="h-3.5 w-3.5" />}
                collapsible={false}
                trailing={
                  <ReviewEditButton
                    onClick={() => goToStep(editStepFor("host"))}
                  />
                }
              >
                <PartnerProfilePanel partnership={reviewPartner} />
              </DetailCard>
            );
          }

          if (section === "product") {
            return (
              <DetailCard
                key="product"
                title="Product"
                icon={<Package className="h-3.5 w-3.5" />}
                collapsible
                previewCount={6}
                trailing={
                  <ReviewEditButton
                    onClick={() => goToStep(editStepFor("product"))}
                  />
                }
              >
                {holdingCardLoading ? (
                  <CardConfigLoading />
                ) : (
                  <>
                    {/* The product name is the record's identity - always render it
                    as a dedicated row, whether or not the tenant configured
                    product columns, so a configured card (e.g. OC's "Color")
                    never hides the name. */}
                    {productDisplayName ? (
                      <FieldRow
                        key="product"
                        label="Product"
                        value={productDisplayName}
                      />
                    ) : null}
                    {configuredProductReviewRows.length > 0
                      ? // Card display config takes priority: render the configured
                        // columns alone (either-or, not additive). The product
                        // template's own attributes surface only in the no-config
                        // fallback branch below.
                        configuredProductReviewRows
                          .filter(
                            (row) =>
                              row.value !== null &&
                              row.value !== undefined &&
                              row.value !== "" &&
                              row.value !== "-",
                          )
                          .map((row) => (
                            <FieldRow
                              key={row.key}
                              label={row.label}
                              value={row.value}
                            />
                          ))
                      : [
                          selectedProduct?.product_template?.name ? (
                            <FieldRow
                              key="template"
                              label="Template"
                              value={selectedProduct.product_template.name}
                            />
                          ) : null,
                          selectedProduct?.brand?.name ? (
                            <FieldRow
                              key="brand"
                              label="Brand"
                              value={selectedProduct.brand.name}
                            />
                          ) : null,
                          selectedProduct?.category?.name ? (
                            <FieldRow
                              key="category"
                              label="Category"
                              value={selectedProduct.category.name}
                            />
                          ) : null,
                          (() => {
                            const pd = selectedProduct?.product_data as
                              Record<string, unknown> | undefined;
                            const sku = pd?.sku ?? pd?.SKU;
                            return sku !== undefined &&
                              sku !== null &&
                              String(sku).trim() !== "" ? (
                              <FieldRow
                                key="sku"
                                label="SKU"
                                value={String(sku)}
                              />
                            ) : null;
                          })(),
                          productVariantDisplayName ? (
                            <FieldRow
                              key="color"
                              label="Color"
                              value={productVariantDisplayName}
                            />
                          ) : null,
                          /* WPS-114: install date is rendered authoritatively on the
                       Purchase card (as "Start/Installation Date"); not repeated
                       here to avoid a duplicate row. */
                          installedComponents ? (
                            <FieldRow
                              key="installed-components"
                              label="Installed components"
                              value={installedComponents}
                            />
                          ) : null,
                          selectedClass?.warranty_type ? (
                            <FieldRow
                              key="warranty-type"
                              label="Warranty type"
                              value={formatWarrantyTypeLabel(
                                selectedClass.warranty_type,
                              )}
                            />
                          ) : null,
                          /* No card display config: fall back to the product
                       template's own attributes (non-blank only). */
                          ...productTemplateAttrRows.map((row) => (
                            <FieldRow
                              key={`ptattr-${row.key}`}
                              label={row.label}
                              value={row.value}
                            />
                          )),
                        ]}
                    {reviewFieldsForSection("product")
                      .filter((f) => f.field_type !== "BOOLEAN")
                      .map((field) => (
                        <FieldRow
                          key={field.uuid}
                          label={field.display_name}
                          value={formatRegistrationFieldValue(
                            field,
                            state.registrationFieldValues[field.code_name],
                            { locale, currency },
                          )}
                        />
                      ))}
                  </>
                )}
              </DetailCard>
            );
          }

          if (section === "warranty") {
            if (!selectedClass) return null;
            return (
              <DetailCard
                key="warranty"
                title="Warranty"
                icon={<ShieldCheck className="h-3.5 w-3.5" />}
                collapsible={false}
                trailing={
                  <ReviewEditButton
                    onClick={() => goToStep(editStepFor("warranty"))}
                  />
                }
              >
                <FieldRow label="Class" value={selectedClass.name} />
                <FieldRow
                  label="Type"
                  value={formatWarrantyTypeLabel(selectedClass.warranty_type)}
                />
                <FieldRow label="Duration" value={selectedWarrantyDuration} />
                {/* Default fallback: the warranty template's own attributes
                    (option override, else template default), non-blank only. */}
                {warrantyTemplateAttrRows.map((row) => (
                  <FieldRow
                    key={`wtattr-${row.key}`}
                    label={row.label}
                    value={row.value}
                  />
                ))}
                {reviewFieldsForSection("warranty").map((field) => (
                  <FieldRow
                    key={field.uuid}
                    label={field.display_name}
                    value={formatRegistrationFieldValue(
                      field,
                      state.registrationFieldValues[field.code_name],
                      { locale, currency },
                    )}
                  />
                ))}
              </DetailCard>
            );
          }

          if (section === "purchase") {
            return (
              <DetailCard
                key="purchase"
                title="Purchase"
                icon={<Receipt className="h-3.5 w-3.5" />}
                collapsible={false}
                trailing={
                  <ReviewEditButton
                    onClick={() => goToStep(editStepFor("purchase"))}
                  />
                }
              >
                {holdingCardLoading ? (
                  <CardConfigLoading />
                ) : (
                  <>
                    {installationDate ? (
                      <FieldRow
                        key="wps114-install-date"
                        label={installationDateLabel}
                        value={installationDate}
                      />
                    ) : null}
                    {warrantyStartDisplay ? (
                      <FieldRow
                        key="wps114-warranty-start"
                        label={warrantyStartLabel}
                        value={warrantyStartDisplay}
                      />
                    ) : null}
                    {configuredPurchaseReviewRows.length > 0 ? (
                      // "If not collected, don't display" also means: a field the
                      // step collected but the user left blank must not render as an
                      // empty "-" row (formatWizardDisplayColumnValue returns "-" for
                      // null/undefined/""). Drop those, mirroring the Product card
                      // above. The branch condition still keys on the presence of
                      // configured columns, so an all-blank card simply shows the
                      // always-on rows (install date, warranty start) and nothing else,
                      // rather than falling through to the generic fallback.
                      configuredPurchaseReviewRows
                        .filter(
                          (row) =>
                            row.value !== null &&
                            row.value !== undefined &&
                            row.value !== "" &&
                            row.value !== "-",
                        )
                        .map((row) => (
                          <FieldRow
                            key={row.key}
                            label={row.label}
                            value={row.value}
                          />
                        ))
                    ) : sortedTransactionAttrs.length > 0 ? (
                      <>
                        {sortedTransactionAttrs.map((attr) => {
                          if (
                            WPS114_EXPLICIT_PURCHASE_CODES.has(attr.code_name)
                          ) {
                            return null;
                          }
                          const val = resolveTransactionAttrValue(
                            state,
                            attr.code_name,
                          );
                          const display = formatTransactionAttrValue(
                            attr,
                            val,
                            { locale, currency },
                          );
                          if (display === null) return null;
                          return (
                            <FieldRow
                              key={attr.uuid}
                              label={attr.display_name}
                              value={display}
                            />
                          );
                        })}
                        {state.partnershipName ? (
                          <FieldRow
                            label={contractorRoleName}
                            value={state.partnershipName}
                          />
                        ) : null}
                        {subcontractorName ? (
                          <FieldRow
                            label="Subcontractor"
                            value={subcontractorName}
                          />
                        ) : null}
                      </>
                    ) : (
                      <>
                        {state.purchaseDate ? (
                          <FieldRow
                            label="Date"
                            value={formatDateLong(state.purchaseDate)}
                          />
                        ) : null}
                        {/* Warranty start is rendered above (WPS-114) from the
                        install-date-aware cascade; not repeated here. */}
                        {state.purchasePrice ? (
                          <FieldRow
                            label="Price"
                            value={fmtCurrencyStr(
                              state.purchasePrice,
                              formatCurrency,
                            )}
                          />
                        ) : null}
                        {state.partnershipName ? (
                          <FieldRow
                            label={contractorRoleName}
                            value={state.partnershipName}
                          />
                        ) : null}
                        {subcontractorName ? (
                          <FieldRow
                            label="Subcontractor"
                            value={subcontractorName}
                          />
                        ) : null}
                        {state.salesPerson ? (
                          <FieldRow
                            label="Sales person"
                            value={state.salesPerson}
                          />
                        ) : null}
                        {state.salesRefNumber ? (
                          <FieldRow
                            label="Reference"
                            value={state.salesRefNumber}
                          />
                        ) : null}
                      </>
                    )}
                    {reviewFieldsForSection("purchase").map((field) => (
                      <FieldRow
                        key={field.uuid}
                        label={field.display_name}
                        value={formatRegistrationFieldValue(
                          field,
                          state.registrationFieldValues[field.code_name],
                          { locale, currency },
                        )}
                      />
                    ))}
                  </>
                )}
              </DetailCard>
            );
          }

          if (section === "customer") {
            const customerStepExists = segments.some((s) =>
              ["customer", "purchase_customer"].includes(s),
            );
            return (
              <DetailCard
                key="customer"
                testId="review-customer-card"
                title="Customer"
                icon={<UserCircle className="h-3.5 w-3.5" />}
                collapsible={false}
                trailing={
                  mergeReplacementMatch || !customerStepExists ? undefined : (
                    <ReviewEditButton
                      onClick={() => goToStep(editStepFor("customer"))}
                    />
                  )
                }
              >
                {/* Render each customer row only when it has a value, so the
                    review matches what the wizard's Customer step asked.
                    Tenants whose data_capture_config excludes a field will
                    have an empty value here and the row stays hidden. */}
                {displayCustomerName ? (
                  <FieldRow label="Name" value={displayCustomerName} />
                ) : null}
                {displayCustomerEmail ? (
                  <FieldRow label="Email" value={displayCustomerEmail} />
                ) : null}
                {registeredByEmail ? (
                  <FieldRow
                    label={t("registrations.registeredBy", "Registered by")}
                    value={registeredByEmail}
                  />
                ) : null}
                {displayCustomerPhone ? (
                  <FieldRow label="Phone" value={displayCustomerPhone} />
                ) : null}
                {displayCustomerAddress ? (
                  <FieldRow label="Address" value={displayCustomerAddress} />
                ) : null}
                {reviewFieldsForSection("customer").map((field) => (
                  <FieldRow
                    key={field.uuid}
                    label={field.display_name}
                    value={formatRegistrationFieldValue(
                      field,
                      state.registrationFieldValues[field.code_name],
                      { locale, currency },
                    )}
                  />
                ))}
              </DetailCard>
            );
          }

          const fields = reviewFieldsForSection("registration_details");
          if (fields.length === 0) return null;
          return (
            <DetailCard
              key="registration_details"
              title="Registration details"
              icon={<AlertTriangle className="h-3.5 w-3.5" />}
              collapsible={false}
              trailing={
                <ReviewEditButton
                  onClick={() => goToStep(editStepFor("registration_details"))}
                />
              }
            >
              {fields.map((field) => (
                <FieldRow
                  key={field.uuid}
                  label={field.display_name}
                  value={formatRegistrationFieldValue(
                    field,
                    state.registrationFieldValues[field.code_name],
                    { locale, currency },
                  )}
                />
              ))}
            </DetailCard>
          );
        })}
      </div>
      {/* ── Disclaimers / terms acknowledgement (last gate before submit) ── */}
      {reviewDisclaimerFields.length > 0 && (
        <div className="mt-3">
          <RegistrationFieldsPanel
            fields={reviewDisclaimerFields}
            values={disclaimerFormValues}
            onChange={handleDisclaimerChange}
            variant="inline"
          />
          {disclaimerAttempted && unmetDisclaimers.length > 0 && (
            <p className="mt-3 text-[11px] text-[var(--status-error)]">
              {unmetDisclaimers.length === 1
                ? `${unmetDisclaimers[0].display_name} is required`
                : "Please acknowledge all required terms before submitting."}
            </p>
          )}
        </div>
      )}
      <div className="mt-3">
        <ConsumerConsentFields
          config={metadata?.consents}
          values={state.consents}
          invalid={consentInvalid}
          onChange={(kind, checked) => {
            update({ consents: { ...state.consents, [kind]: checked } });
            if (checked) setConsentInvalid((prev) => prev.filter((k) => k !== kind));
          }}
        />
      </div>
      {/* ── Actions ── */}
      <div
        className={`mt-4 flex items-center ${back ? "justify-between" : "justify-end"}`}
      >
        {back && (
          <Button
            type="button"
            variant="ghost"
            onClick={back}
            disabled={isSubmitting}
            className="flex cursor-pointer items-center gap-1.5 px-0 text-[11px] font-light uppercase tracking-[0.1em] text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)] disabled:opacity-40"
          >
            &larr; Back
          </Button>
        )}

        <Button
          type="button"
          onClick={handleConfirm}
          disabled={isSubmitting || Boolean(submissionBlockedMessage)}
          className="inline-flex cursor-pointer items-center gap-2 bg-[var(--green)] px-6 py-2.5 text-[11px] font-light uppercase tracking-[0.1em] text-[var(--bg)] transition-opacity hover:opacity-90 disabled:opacity-65"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-[14px] w-[14px] animate-spin" />
              Registering&hellip;
            </>
          ) : (
            "Confirm & Register"
          )}
        </Button>
      </div>
      {submissionBlockedMessage ? (
        <p className="mt-3 text-right text-[11px] text-[var(--status-error)]">
          {submissionBlockedMessage}
        </p>
      ) : null}
      <DuplicateAddressDecisionModal
        open={!!resolvingIssueId && !!duplicateAddressIssue}
        issue={duplicateAddressIssue}
        propertyAddress={propertyAddress}
        duplicateMatches={reviewDuplicateMatches}
        hostLookupData={state.hostAttributeValues}
        onClose={() => setResolvingIssueId(null)}
        onResolve={handleResolveDuplicateIssue}
      />
      <ConfirmationDialog
        open={approvalConfirmationOpen}
        onOpenChange={(open) => setApprovalConfirmationOpen(open)}
        title="Approval required before this activates"
        description={
          <div className="space-y-3">
            <p className="m-0">
              This registration matches one or more approval rules and will be
              created as a <span className="font-light">draft</span>. The
              lifecycle workflow will automatically open a workflow issue that
              an approver must resolve before the warranty becomes active.
            </p>
            {submissionApprovalHits.length > 0 ? (
              <div className="flex flex-col gap-2">
                {submissionApprovalHits.map((hit) => (
                  <div
                    key={hit.rule_uuid}
                    className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2"
                  >
                    <p className="m-0 text-[12.5px] font-light text-[var(--ink)]">
                      {hit.name}
                    </p>
                    {hit.message && hit.message !== hit.name ? (
                      <p className="m-0 mt-1 text-[12px] leading-[1.45] text-[var(--ink-3)]">
                        {hit.message}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
            <p className="m-0 text-[12px] text-[var(--ink-3)]">
              You'll find the pending issue on the registration's workflow tab,
              and it'll also appear in the Approvals queue for the relevant
              resolver roles.
            </p>
          </div>
        }
        cancelLabel="Edit registration"
        confirmLabel="Continue"
        pendingLabel="Submitting…"
        isPending={isSubmitting}
        onConfirm={handleConfirmApproval}
      />
      <ConfirmationDialog
        open={warningConfirmationOpen}
        onOpenChange={(open) => setWarningConfirmationOpen(open)}
        title="Registration needs review"
        description={
          <div className="space-y-3">
            <p className="m-0">
              This registration matches one or more validation rules. Review the
              details below, then edit the registration or continue.
            </p>
            <div className="flex flex-col gap-2">
              {submissionWarningHits.map((hit) => (
                <div
                  key={hit.rule_uuid}
                  className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2"
                >
                  <p className="m-0 text-[12.5px] font-light text-[var(--ink)]">
                    {hit.name}
                  </p>
                  <p className="m-0 mt-1 text-[12px] leading-[1.45] text-[var(--ink-3)]">
                    {hit.message}
                  </p>
                </div>
              ))}
            </div>
          </div>
        }
        cancelLabel="Edit registration"
        confirmLabel="Continue"
        pendingLabel="Submitting…"
        isPending={isSubmitting}
        onConfirm={handleConfirmWarnings}
      />
      <ConfirmationDialog
        open={cancelledConfirmationOpen}
        onOpenChange={(open) => setCancelledConfirmationOpen(open)}
        title="Register as cancelled?"
        description={
          <div className="space-y-2">
            <p className="m-0">
              This duplicate-address decision will create the registration with
              status <span className="font-light">Cancelled</span>.
            </p>
            <p className="m-0">
              Continue only if this registration should be kept as a cancelled
              record for exception review.
            </p>
          </div>
        }
        cancelLabel="Discard changes"
        confirmLabel="Confirm and continue"
        pendingLabel="Registering..."
        isPending={isSubmitting}
        onConfirm={handleConfirmCancelledRegistration}
      />
    </div>
  );
}
